/**
 * Rithmo API Service - REST API client for the backend server.
 */

import {
  WHITEPAPER_DOWNLOAD_SUBJECT,
  formatWhitepaperRequestMessage,
  contactUsSubject,
  formatContactUsMessage,
} from '../constants/supportTicketSubjects';

const viteEnv = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

/** Backend mounts REST under `/api`. Accepts `http://host:port` in VITE_API_URL and normalizes to `.../api`. */
function resolveApiBaseUrl(): string {
  const fallback = 'http://localhost:5000/api';
  const raw = viteEnv.VITE_API_URL?.trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/, '');
  if (noTrailingSlash.endsWith('/api')) return noTrailingSlash;
  if (noTrailingSlash.startsWith('/')) {
    if (noTrailingSlash === '/api' || noTrailingSlash.endsWith('/api')) return noTrailingSlash;
    const base = noTrailingSlash.replace(/\/+$/, '');
    if (base === '' || base === '/') return '/api';
    return `${base}/api`;
  }
  try {
    const u = new URL(noTrailingSlash);
    if (!u.pathname || u.pathname === '/') {
      return `${u.origin}/api`;
    }
  } catch {
    /* non-URL string */
  }
  return `${noTrailingSlash}/api`;
}

const API_BASE_URL = resolveApiBaseUrl();

/** Set `VITE_DEBUG_PREP_ATTACHMENTS=1` (or `true`) to log meeting-prep / screenshare client diagnostics. */
export function debugPrepAttachments(label: string, details?: Record<string, unknown>): void {
  if (viteEnv.VITE_DEBUG_PREP_ATTACHMENTS !== '1' && viteEnv.VITE_DEBUG_PREP_ATTACHMENTS !== 'true') return;
  console.debug('[prep-attachments]', label, details ?? '');
}

/**
 * When the API responds 404 from screenshare PUT with `error.details.screenshareStatusLookupMiss`:
 * backend must enable `RITHMO_SCREENSHARE_STATUS_DEBUG=true` (or `1`) — NEVER in publicly reachable production unless you intend to leak UUIDs/route ids.
 *
 * Frontend: enable `VITE_SURFACE_SCREENSHARE_404_DETAILS=1` — `DocumentIntelligencePanel` copies the miss payload onto `window.__RITHMO_SCREENSHARE_LOOKUP_MISS` for inspector-only debugging (Network remains the canonical view).
 */
export function viteSurfaceScreenshare404Details(): boolean {
  return viteEnv.VITE_SURFACE_SCREENSHARE_404_DETAILS === '1' || viteEnv.VITE_SURFACE_SCREENSHARE_404_DETAILS === 'true';
}

/** Server origin (no /api path) for OAuth redirect URL. */
export const getServerOrigin = () => (API_BASE_URL || '').replace(/\/api\/?$/, '') || (typeof window !== 'undefined' ? window.location.origin : '');

/** URL to start Zoom OAuth — redirects directly to Zoom's authorize page. */
export const getConnectZoomUrl = (userId?: string | number | null) => {
  if (!userId) return null;
  const clientId = viteEnv.VITE_ZOOM_APP_CLIENT_ID;
  const publicUrl = viteEnv.VITE_PUBLIC_URL || getServerOrigin();
  if (!clientId) return null;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: `${publicUrl.replace(/\/$/, '')}/zoom/callback`,
    state: String(userId),
  });
  return `https://zoom.us/oauth/authorize?${params.toString()}`;
};

let authErrorHandler: (() => void) | null = null;

export function setAuthErrorHandler(handler: (() => void) | null): void {
  authErrorHandler = handler;
}

async function apiRequest(endpoint: string, options: RequestInit = {}): Promise<unknown> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('rithmo_token') : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Ngrok free tier shows an interstitial for browser requests; this header skips it
  if (url.includes('ngrok-free.dev') || url.includes('ngrok.io')) {
    headers['ngrok-skip-browser-warning'] = 'true';
  }
  const config: RequestInit = { ...options, headers };
  try {
    const response = await fetch(url, config);
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: { message?: string; code?: string } | string;
        code?: string;
      };
      const errMessage =
        typeof body.error === 'object' && body.error !== null
          ? body.error.message
          : typeof body.error === 'string'
            ? body.error
            : undefined;
      const message = errMessage ?? body.message ?? `HTTP ${response.status}`;
      const err = new Error(message) as Error & { status?: number; code?: string; body?: unknown };
      err.status = response.status;
      const nestedCode =
        typeof body.error === 'object' && body.error !== null && typeof body.error.code === 'string'
          ? body.error.code
          : undefined;
      if (nestedCode) err.code = nestedCode;
      else if (typeof body.code === 'string') err.code = body.code;
      err.body = body;
      // 401 handling — only treat as a session-invalid event if it's actually
      // a session/token issue. Per-resource permission denials (e.g. you opened
      // a meeting you don't own and an authz check failed) should NOT log the
      // user out — the component-level catch should handle it. Backend now
      // uses 403 for permission denials, but keep a defensive guard here so a
      // single misused 401 anywhere can never force-logout the user.
      if (response.status === 401) {
        const sessionInvalidCodes = new Set([
          'TOKEN_INVALID',
          'TOKEN_EXPIRED',
          'TOKEN_REVOKED',
          'AUTH_REQUIRED',
          'SESSION_EXPIRED',
        ]);
        const sessionPaths = ['/auth/me', '/auth/verify', '/auth/refresh'];
        const isSessionEndpoint = sessionPaths.some((p) => endpoint.startsWith(p));
        const messageLooksSessionInvalid = /authentication required|session expired|invalid token|token expired/i.test(
          message,
        );
        const triggerLogout =
          isSessionEndpoint ||
          (err.code != null && sessionInvalidCodes.has(err.code)) ||
          messageLooksSessionInvalid;
        if (triggerLogout) {
          authErrorHandler?.();
        }
      }
      throw err;
    }
    return response.json();
  } catch (error: unknown) {
    console.error(`API Error [${endpoint}]:`, error instanceof Error ? error : String(error));
    throw error;
  }
}

/** Multipart POST (meeting attachments). Do not set Content-Type — boundary is set automatically. */
async function apiRequestMultipart(endpoint: string, formData: FormData): Promise<unknown> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = new Headers();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('rithmo_token') : null;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (url.includes('ngrok-free.dev') || url.includes('ngrok.io')) {
    headers.set('ngrok-skip-browser-warning', 'true');
  }
  try {
    const response = await fetch(url, { method: 'POST', body: formData, headers });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string | { message?: string; code?: string };
      };
      const errMessage =
        typeof body.error === 'object' && body.error !== null
          ? body.error.message
          : typeof body.error === 'string'
            ? body.error
            : undefined;
      const message = errMessage ?? body.message ?? `HTTP ${response.status}`;
      const err = new Error(message) as Error & { status?: number; code?: string };
      err.status = response.status;
      if (typeof body.error === 'object' && body.error !== null && typeof body.error.code === 'string') {
        err.code = body.error.code;
      }
      throw err;
    }
    return response.json();
  } catch (error: unknown) {
    console.error(`API Error [${endpoint}]:`, error instanceof Error ? error : String(error));
    throw error;
  }
}

export const slackHuddleAPI = {
  /** Whether a huddle session is connected for this workspace + if the caller is an org admin. */
  getStatus: () => apiRequest('/integrations/slack/huddle/status'),
  /** Org-admin: capture + store the member session via login (guided login-capture). */
  connect: (data: { host: string; email: string; password: string; totp?: string }) =>
    apiRequest('/integrations/slack/huddle/connect', { method: 'POST', body: JSON.stringify(data) }),
  /** Org-admin: revoke the stored session. */
  disconnect: () => apiRequest('/integrations/slack/huddle/disconnect', { method: 'POST' }),
};

export const zoomAutoCaptureAPI = {
  /** Whether auto-capture is enabled for the caller's org + if the caller is an org admin. */
  getStatus: () => apiRequest('/integrations/zoom/auto-capture/status'),
  /** Org-admin: enable/disable auto-capture of connected users' scheduled Zoom meetings. */
  setEnabled: (enabled: boolean) =>
    apiRequest('/integrations/zoom/auto-capture', { method: 'POST', body: JSON.stringify({ enabled }) }),
};

export const authAPI = {
  login: (email: string, password: string, timezone?: string | null) =>
    apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(
        timezone ? { email, password, timezone: timezone.trim() || undefined } : { email, password }
      ),
    }),
  signup: (data: object) =>
    apiRequest('/auth/signup', { method: 'POST', body: JSON.stringify(data) }),
  logout: () => apiRequest('/auth/logout', { method: 'POST' }),
  getProfile: () => apiRequest('/auth/profile'),
  updateProfile: (data: object) =>
    apiRequest('/auth/profile', { method: 'PATCH', body: JSON.stringify(data) }),
  changePassword: (data: { old_password: string; new_password: string; confirm_password: string }) =>
    apiRequest('/auth/change-password', { method: 'POST', body: JSON.stringify(data) }),
  getNotifications: () =>
    apiRequest('/auth/notifications'),
  updateNotifications: (data: {
    meetingReminders?: boolean
    weeklyReport?: boolean
    postMeetingSummary?: boolean
    marketingEmails?: boolean
    meetingInvitations?: boolean
    dailyDigest?: boolean
  }) =>
    apiRequest('/auth/notifications', {
      method: 'PATCH',
      body: JSON.stringify({
        meeting_reminders: data.meetingReminders,
        weekly_report: data.weeklyReport,
        post_meeting_summary: data.postMeetingSummary,
        marketing_emails: data.marketingEmails,
        meeting_invitations: data.meetingInvitations,
        daily_digest: data.dailyDigest,
      }),
    }),
  getGovernance: () =>
    apiRequest('/auth/governance'),
  updateGovernance: (data: {
    talkEquity?: { enabled?: boolean; targetRatio?: number }
    interruptionPrevention?: { enabled?: boolean; threshold?: number }
    agendaEnforcement?: { enabled?: boolean; strictMode?: boolean }
  }) =>
    apiRequest('/auth/governance', {
      method: 'PATCH',
      body: JSON.stringify({
        talk_equity_enabled: data.talkEquity?.enabled,
        talk_equity_target_ratio: data.talkEquity?.targetRatio,
        interruption_prevention_enabled: data.interruptionPrevention?.enabled,
        interruption_prevention_threshold: data.interruptionPrevention?.threshold,
        agenda_enforcement_enabled: data.agendaEnforcement?.enabled,
        agenda_enforcement_strict_mode: data.agendaEnforcement?.strictMode,
      }),
    }),
  verifyEmail: (otp: string) =>
    apiRequest('/auth/verify-email', { method: 'POST', body: JSON.stringify({ otp }) }),
  resendOtp: () =>
    apiRequest('/auth/resend-otp', { method: 'POST' }),
  forgotPassword: (email: string) =>
    apiRequest('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  verifyResetOtp: (reset_token: string, otp: string) =>
    apiRequest('/auth/verify-reset-otp', { method: 'POST', body: JSON.stringify({ reset_token, otp }) }),
  resendResetOtp: (reset_token: string) =>
    apiRequest('/auth/resend-reset-otp', { method: 'POST', body: JSON.stringify({ reset_token }) }),
  resetPassword: (reset_token: string, new_password: string, confirm_password: string) =>
    apiRequest('/auth/reset-password', { method: 'POST', body: JSON.stringify({ reset_token, new_password, confirm_password }) }),
  getKnownContacts: () =>
    apiRequest('/auth/known-contacts') as Promise<{ contacts?: Array<{ email: string; display_name: string }> }>,
  upsertKnownContact: (email: string, displayName: string) =>
    apiRequest('/auth/known-contacts', {
      method: 'POST',
      body: JSON.stringify({ email, display_name: displayName }),
    }),
  saveScoreGuess: (guess: number) =>
    apiRequest('/auth/scorecard-guess', { method: 'PATCH', body: JSON.stringify({ guess }) }),
};

export interface FollowupEmailDraftFact {
  text: string;
  type: string;
  sourceMeetingId: string | null;
}

export interface FollowupEmailDraft {
  id: string;
  meetingId: string;
  participantId: string;
  recipientDisplayName: string | null;
  recipientEmail: string | null;
  instruction: string | null;
  subject: string;
  body: string;
  tone: 'warm' | 'concise' | 'formal';
  usedFacts: FollowupEmailDraftFact[];
  edited: boolean;
  model: string | null;
  status: 'ready' | 'failed';
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FollowupDraftChatTurn {
  id: string;
  role: 'user' | 'assistant';
  message: string;
  subject: string | null;
  body: string | null;
  createdAt: string;
}

export interface ParticipantEnrichmentHeadline {
  title: string;
  source: string;
  url: string;
  publishedAt: string | null;
}

export interface ParticipantEnrichmentSource {
  url: string;
  title: string | null;
}

export interface ParticipantEnrichment {
  id: string;
  meetingId: string;
  participantId: string;
  subjectDisplayName: string | null;
  subjectEmail: string | null;
  resolvedFullName: string | null;
  resolvedCompany: string | null;
  headlines: ParticipantEnrichmentHeadline[];
  bio: string;
  recentActivity: string;
  confidence: 'high' | 'low';
  disambiguationNote: string | null;
  sources: ParticipantEnrichmentSource[];
  model: string | null;
  status: 'ready' | 'failed' | 'needs_identity';
  errorMessage: string | null;
  createdAt: string;
  expiresAt: string;
  updatedAt: string;
  stale: boolean;
}

export const meetingsAPI = {
  getPresets: () => apiRequest('/meeting/presets'),
  init: (meetingId: string, data: object) =>
    apiRequest(`/meeting/${meetingId}/init`, { method: 'POST', body: JSON.stringify(data) }),
  get: (meetingId: string) => apiRequest(`/meetings/${meetingId}`),
  /** Recap, analytics rollup, delivery status (for completed meetings). */
  getDetails: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/details`) as Promise<{
      data?: {
        meeting_id: string;
        analytics?: Record<string, unknown>;
        latest_recap?: unknown;
        latest_analytics_rollup?: { content?: unknown };
      };
    }>,
  /** Product-approved participant context bullets for Personal Insights. */
  getParticipantContextMemory: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/participant-context-memory`) as Promise<{
      data?: {
        meetingId: string;
        participants: Array<{
          participantId: string;
          userId: string | null;
          displayName: string | null;
          contextBullets: string[];
          contextBulletItems?: Array<{
            memoryId: string;
            text: string;
          }>;
        }>;
      };
    }>,
  deleteParticipantContextMemory: (meetingId: string, participantId: string, memoryId: string) =>
    apiRequest(
      `/meetings/${encodeURIComponent(meetingId)}/participant-context-memory/${encodeURIComponent(participantId)}/${encodeURIComponent(memoryId)}`,
      { method: 'DELETE' },
    ) as Promise<{
      data?: {
        ok: true;
        meetingId: string;
        participantId: string;
        memoryId: string;
        scrubbedCount: number;
      };
    }>,
  /**
   * Meeting-prep participant cards. `version: 2` is the substance prep card
   * (who they are / open commitments / cares about / last time); `version: 1`
   * is the legacy telemetry narrative (served only when v2 is disabled).
   */
  getParticipantPrepProfiles: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/participant-prep-profiles`) as Promise<{
      data?: {
        meetingId: string;
        version?: 1 | 2;
        participants: Array<{
          participantId: string;
          userId: string | null;
          displayName: string | null;
          // v2 substance card
          sharedMeetingCount?: number;
          whoTheyAre?: Array<{ text: string; type: string; sourceMeetingId: string }>;
          theyOwe?: Array<{
            id: string;
            summary: string;
            dueDateText: string | null;
            dueDateIso: string | null;
            isOverdue: boolean;
            status: string;
            sourceMeetingId: string;
            sourceMeetingTitle: string | null;
            markedPriority?: boolean;
            priorityTier?: 'critical' | 'high' | 'medium' | 'low';
            priorityReasons?: string[];
          }>;
          youOwe?: Array<{
            id: string;
            summary: string;
            dueDateText: string | null;
            dueDateIso: string | null;
            isOverdue: boolean;
            status: string;
            sourceMeetingId: string;
            sourceMeetingTitle: string | null;
            markedPriority?: boolean;
            priorityTier?: 'critical' | 'high' | 'medium' | 'low';
            priorityReasons?: string[];
          }>;
          caresAbout?: Array<{ text: string; type: string; sourceMeetingId: string }>;
          lastMeeting?: {
            meetingId: string;
            title: string | null;
            date: string | null;
          } | null;
          // v1 legacy narrative (only present when version === 1)
          aiNarrative?: string | null;
          participationTrend?: string | null;
          meetingCount?: number;
          confidenceScore?: number;
          maturityLevel?: 'nascent' | 'emerging' | 'mature';
          communicationStyle?: {
            verbosity: 'concise' | 'moderate' | 'detailed';
            interruptionPattern: 'frequent_interrupter' | 'occasionally_interrupted' | 'balanced';
            engagementTrend: 'increasing' | 'steady' | 'declining' | 'new_participant';
          } | null;
          relationshipContext?: {
            sharedMeetingCount: number;
            lastSharedMeetingTitle: string | null;
            lastSharedMeetingDate: string | null;
          } | null;
        }>;
        strategyContext?: Array<{
          id: string;
          summary: string;
          theme: string | null;
          polarity: 'affirmative' | 'negative';
          status: 'active' | 'evolving';
          decidedAtMs: number | null;
        }>;
      };
    }>,
  /** Set/clear the human "mark priority" flag on a ledger commitment. */
  setCommitmentPriority: (commitmentId: string, marked: boolean) =>
    apiRequest(`/dashboard/commitments/${commitmentId}/priority`, {
      method: 'PATCH',
      body: JSON.stringify({ marked }),
    }) as Promise<{ data?: { commitmentId: string; markedPriority: boolean } }>,
  /** Persist the user's manual ordering of commitments (Prep Notes drag-reorder). */
  setCommitmentOrder: (orderedCommitmentIds: string[]) =>
    apiRequest(`/dashboard/commitments/order`, {
      method: 'PUT',
      body: JSON.stringify({ orderedCommitmentIds }),
    }) as Promise<{ data?: { written: number } }>,
  /**
   * Follow-up email drafts (on-demand, per-recipient, draft-only).
   * List returns each candidate recipient with their current draft inlined (or null).
   */
  listFollowupDrafts: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/followup-drafts`) as Promise<{
      data?: {
        meetingId: string;
        recipients: Array<{
          participantId: string;
          displayName: string | null;
          email: string | null;
          draft: FollowupEmailDraft | null;
        }>;
      };
    }>,
  /** Generate (or regenerate) a draft for one recipient. instruction is required:
   *  the sender's free-text intent for what the email should accomplish. */
  generateFollowupDraft: (
    meetingId: string,
    participantId: string,
    instruction: string,
    tone?: 'warm' | 'concise' | 'formal',
  ) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/followup-draft`, {
      method: 'POST',
      body: JSON.stringify({ participantId, instruction, ...(tone ? { tone } : {}) }),
    }) as Promise<{ data?: { draft: FollowupEmailDraft } }>,
  /** Persist sender hand-edits to an existing draft. */
  saveFollowupDraft: (meetingId: string, participantId: string, subject: string, body: string) =>
    apiRequest(
      `/meetings/${encodeURIComponent(meetingId)}/followup-draft/${encodeURIComponent(participantId)}`,
      { method: 'PUT', body: JSON.stringify({ subject, body }) },
    ) as Promise<{ data?: { draft: FollowupEmailDraft } }>,
  /** Load the refinement chat for one recipient's draft (oldest turn first). */
  getFollowupChat: (meetingId: string, participantId: string) =>
    apiRequest(
      `/meetings/${encodeURIComponent(meetingId)}/followup-draft/${encodeURIComponent(participantId)}/chat`,
    ) as Promise<{ data?: { meetingId: string; participantId: string; turns: FollowupDraftChatTurn[] } }>,
  /** Refine the draft via one chat turn; returns the revised draft, reply, and full chat. */
  sendFollowupChat: (meetingId: string, participantId: string, message: string) =>
    apiRequest(
      `/meetings/${encodeURIComponent(meetingId)}/followup-draft/${encodeURIComponent(participantId)}/chat`,
      { method: 'POST', body: JSON.stringify({ message }) },
    ) as Promise<{ data?: { draft: FollowupEmailDraft; reply: string; turns: FollowupDraftChatTurn[] } }>,
  /**
   * Participant enrichment (bios + recent news on external participants, Prep tab).
   * List returns external participants with their current enrichment inlined (or null);
   * `enabled` is false when PARTICIPANT_ENRICHMENT_ENABLED is off.
   */
  listParticipantEnrichments: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/participant-enrichments`) as Promise<{
      data?: {
        meetingId: string;
        enabled: boolean;
        participants: Array<{
          participantId: string;
          displayName: string | null;
          email: string | null;
          enrichment: ParticipantEnrichment | null;
        }>;
      };
    }>,
  /** Generate (or regenerate) enrichment for one participant. fullName/company are
   *  supplied when the email alone could not identify the person. */
  generateParticipantEnrichment: (
    meetingId: string,
    participantId: string,
    opts?: { fullName?: string; company?: string },
  ) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/participant-enrichment`, {
      method: 'POST',
      body: JSON.stringify({ participantId, ...(opts ?? {}) }),
    }) as Promise<{ data?: { enrichment?: ParticipantEnrichment; needsIdentity?: boolean } }>,
  /** Shared internal team live state plus viewer-private prompts. */
  getLiveInsights: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/live-insights`) as Promise<{
      data?: {
        schemaVersion: 1;
        meetingId: string;
        dataMode?: 'live' | 'persisted_snapshot';
        sourceMeetingId?: string;
        sourceMeetingEndedAtMs?: number | null;
        sharedTeamView: {
          visibility: 'shared_internal_team';
          activeAgendaItemId: string | null;
          activeAgendaItemTitle: string | null;
          internalTeamMembersOnCall: Array<{
            participantId: string;
            userId: string | null;
            displayName: string | null;
            role: 'owner' | 'member';
            isViewer: boolean;
          }>;
          topicSummary: {
            total: number;
            notStarted: number;
            inProgress: number;
            covered: number;
            coveredWeakConfidence: number;
            atRisk: number;
          };
          topicRows: Array<{
            agendaItemId: string;
            title: string;
            isActive: boolean;
            lifecycleStatus: 'not_started' | 'in_progress' | 'covered' | 'covered_weak_confidence' | 'at_risk';
            statusSummary: string;
            statusReasonCodes: string[];
            evidenceChips: string[];
            decisionCount: number;
            actionItemCount: number;
            followUpCount: number;
            linkedInternalGoalCount: number;
            linkedInternalAtRiskCount: number;
          }>;
          attentionSignals: Array<{
            signalId: string;
            code: string;
            severity: 'info' | 'warning';
            agendaItemId: string | null;
            message: string;
          }>;
          sharedQueue: Array<{
            signalId: string;
            code: string;
            severity: 'info' | 'warning';
            agendaItemId: string | null;
            message: string;
            priority: 'high' | 'medium' | 'low';
            sourceKind: 'shared_team_signal';
            actionState: {
              status: 'open' | 'acknowledged' | 'claimed' | 'resolved' | 'remind_later';
              actedBy: { userId: string | null; displayName: string | null } | null;
              updatedAtMs: number | null;
              remindUntilMs: number | null;
            };
          }>;
          questionSummary: {
            total: number;
            open: number;
            answered: number;
            partiallyAnswered: number;
            answeredByInternalTeam: number;
            answeredByExternalOrUnknown: number;
          };
          liveQuestions: Array<{
            questionId: string;
            questionText: string;
            agendaItemId: string | null;
            agendaItemTitle: string | null;
            askedAtMs: number;
            askedBy: {
              participantId: string | null;
              userId: string | null;
              displayName: string | null;
            };
            askedByScope: 'internal_team' | 'external_or_unknown';
            status: 'open' | 'answered' | 'partially_answered';
            answeredAtMs: number | null;
            answeredBy: {
              participantId: string | null;
              userId: string | null;
              displayName: string | null;
            } | null;
            answeredByScope: 'internal_team' | 'external_or_unknown' | null;
            confidence: number | null;
            questionType: 'clarification' | 'decision_seeking' | 'information_seeking' | 'challenge' | 'follow_up' | null;
            answerQuality: 'full' | 'partial' | 'tangential' | null;
            answerSummary: string | null;
            relatedQuestionId: string | null;
            refinementStatus: 'pending' | 'refined' | 'skipped';
          }>;
          privateGoalSummary: null | {
            total: number;
            notStarted: number;
            inProgress: number;
            covered: number;
            coveredWeakConfidence: number;
            atRisk: number;
          };
        };
        personalView: {
          visibility: 'viewer_private';
          viewerParticipantId: string | null;
          viewerDisplayName: string | null;
          viewerRole: 'meeting_owner' | 'internal_team_member' | 'internal_team_member_unlinked';
          onCall: boolean;
          isCurrentSpeaker: boolean;
          currentStatus: string;
          overallToneSignal: {
            source: 'deterministic_live_tone_proxy' | 'llm_refined';
            status:
              | 'insufficient_evidence'
              | 'constructive'
              | 'neutral'
              | 'negative'
              | 'contentious'
              | 'argumentative';
            intensity: 'low' | 'medium' | 'high';
            summary: string;
            coachingTip: string | null;
            lastUpdatedAtMs: number | null;
          } | null;
          viewerToneSignal: {
            source: 'deterministic_live_tone_proxy' | 'llm_refined';
            status:
              | 'insufficient_evidence'
              | 'constructive'
              | 'neutral'
              | 'negative'
              | 'contentious'
              | 'argumentative';
            intensity: 'low' | 'medium' | 'high';
            summary: string;
            coachingTip: string | null;
            lastUpdatedAtMs: number | null;
          } | null;
          participantToneReads: Array<{
            participantId: string;
            displayName: string | null;
            onCall: boolean;
            isViewer: boolean;
            toneSignal: {
              source: 'deterministic_live_tone_proxy' | 'llm_refined';
              status:
                | 'insufficient_evidence'
                | 'constructive'
                | 'neutral'
                | 'negative'
                | 'contentious'
                | 'argumentative';
              intensity: 'low' | 'medium' | 'high';
              summary: string;
              coachingTip: string | null;
              lastUpdatedAtMs: number | null;
            };
          }>;
          privateTips: string[];
          privateNotifications: Array<{
            notificationId: string;
            code: string;
            severity: 'warning' | 'alert';
            category: 'behavior' | 'time_management';
            sourceActionKind: string;
            agendaItemId: string | null;
            title: string;
            message: string;
            createdAtMs: number;
          }>;
          memberPrivateGoalSummary: null | {
            total: number;
            notStarted: number;
            inProgress: number;
            covered: number;
            coveredWeakConfidence: number;
            atRisk: number;
          };
          memberPrivateGoals: Array<{
            internalAgendaItemId: string;
            linkedPublicAgendaItemId: string | null;
            linkedPublicAgendaTitle: string | null;
            lifecycleStatus: 'not_started' | 'in_progress' | 'covered' | 'covered_weak_confidence' | 'at_risk';
            statusSummary: string;
            statusReasonCodes: string[];
            evidenceChips: string[];
            hasLifecycleOverride: boolean;
          }>;
          personalQueue: Array<{
            itemId: string;
            code: string;
            priority: 'high' | 'medium' | 'low';
            agendaItemId: string | null;
            relatedSharedSignalId: string | null;
            originKind: 'viewer_private_tip';
            message: string;
            actionState: {
              status: 'open' | 'completed' | 'dismissed' | 'snoozed';
              updatedAtMs: number | null;
              remindUntilMs: number | null;
            };
          }>;
        };
      };
    }>,
  postSharedLiveInsightAction: (
    meetingId: string,
    data: {
      signalId: string;
      action: 'acknowledge' | 'claim' | 'resolve' | 'remind_later' | 'reopen' | 'release_claim';
      remindUntilMs?: number | null;
    }
  ) =>
    apiRequest(`/meetings/${meetingId}/live-insights/shared-actions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  postPersonalLiveInsightAction: (
    meetingId: string,
    data: {
      itemId: string;
      action: 'complete' | 'dismiss' | 'snooze' | 'reopen';
      remindUntilMs?: number | null;
    }
  ) =>
    apiRequest(`/meetings/${meetingId}/live-insights/personal-actions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getMemberPrivateGoals: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/member-private-goals`) as Promise<{
      data?: {
        meetingId: string;
        memberPrivateGoals: {
          schemaVersion: 1;
          activeInternalAgendaItemId: string | null;
          items: Array<{
            internalAgendaItemId: string;
            order: number;
            visibilityScope: 'member_private';
            authorUserId: string | null;
            linkedPublicAgendaItemId: string | null;
            desiredOutcome: string;
            targetType: string;
            targetValue: { kind: string };
            concessionBounds: { kind: string };
            redLineType: string;
            redLineParams: { kind: string } | null;
            neverSayTags: string[];
            privateNotes: string | null;
          }>;
        };
      };
    }>,
  putMemberPrivateGoals: (
    meetingId: string,
    data: {
      schemaVersion: 1;
      activeInternalAgendaItemId: string | null;
      items: Array<{
        internalAgendaItemId: string;
        order: number;
        visibilityScope?: 'member_private';
        authorUserId?: string | null;
        linkedPublicAgendaItemId: string | null;
        desiredOutcome: string;
        targetType: string;
        targetValue: { kind: string };
        concessionBounds: { kind: string };
        redLineType: string;
        redLineParams: { kind: string } | null;
        neverSayTags: string[];
        privateNotes: string | null;
      }>;
    }
  ) =>
    apiRequest(`/meetings/${meetingId}/member-private-goals`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }) as Promise<{
      data?: {
        ok: true;
        meetingId: string;
        memberPrivateGoals: {
          schemaVersion: 1;
          activeInternalAgendaItemId: string | null;
          items: Array<{
            internalAgendaItemId: string;
            order: number;
            visibilityScope: 'member_private';
            authorUserId: string | null;
            linkedPublicAgendaItemId: string | null;
            desiredOutcome: string;
            targetType: string;
            targetValue: { kind: string };
            concessionBounds: { kind: string };
            redLineType: string;
            redLineParams: { kind: string } | null;
            neverSayTags: string[];
            privateNotes: string | null;
          }>;
        };
      };
    }>,
  getState: (meetingId: string) => apiRequest(`/meeting/${meetingId}`),
  getStats: (meetingId: string) => apiRequest(`/meeting/${meetingId}/stats`),
  clear: (meetingId: string) => apiRequest(`/meeting/${meetingId}`, { method: 'DELETE' }),
  list: (params: Record<string, string> = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/meetings${query ? `?${query}` : ''}`);
  },
  /** List meetings for import dropdown (supports search by title) */
  listForImport: (search = '') =>
    apiRequest(`/meetings?limit=50${search ? `&search=${encodeURIComponent(search)}` : ''}`) as Promise<{ meetings: Array<{ meetingId: string; title: string }> }>,
  create: (data: object) =>
    apiRequest('/meetings', { method: 'POST', body: JSON.stringify(data) }),
  /** Dynamic coaching copy for meeting builder success screen (quality-based). */
  getBuilderCompletionCoaching: (body: {
    meetingTitle?: string;
    agendaItems?: Array<{ title?: string; details?: string }>;
    meetingDurationMinutes?: number;
    seed?: string;
  }) =>
    apiRequest('/meetings/builder/completion-coaching', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (meetingId: string, data: object) =>
    apiRequest(`/meetings/${meetingId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** Save and send update emails (parent or one-off meetings). Same payload as update. */
  sendUpdate: (meetingId: string, data: object) =>
    apiRequest(`/meetings/${meetingId}/send-update`, { method: 'POST', body: JSON.stringify(data) }),
  updateStatus: (meetingId: string, data: { status: string; startedAtMs?: number; endedAtMs?: number | null }) =>
    apiRequest(`/meetings/${meetingId}/status`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** Convert draft to live meeting (Zoom). Replaces draft with real meeting; removes draft. */
  convertDraftToLive: (meetingId: string, opts?: { enableRecallBot?: boolean; recallTranscriptionOnly?: boolean; organizerTimezone?: string }) =>
    apiRequest(`/meetings/${meetingId}/convert-to-live`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    }),
  delete: (meetingId: string) => apiRequest(`/meetings/${meetingId}`, { method: 'DELETE' }),
  start: (meetingId: string) => apiRequest(`/meetings/${meetingId}/start`, { method: 'POST' }),
  end: (meetingId: string) => apiRequest(`/meetings/${meetingId}/end`, { method: 'POST' }),
  getTranscript: async (
    meetingId: string,
    format: 'txt' | 'pdf' | 'jsonl' = 'txt',
  ): Promise<Blob> => {
    const url = `${API_BASE_URL}/meetings/${meetingId}/transcript?format=${format}`;
    const headers: Record<string, string> = {};
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('rithmo_token') : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    if (url.includes('ngrok-free.dev') || url.includes('ngrok.io')) headers['ngrok-skip-browser-warning'] = 'true';
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { message?: string };
      throw new Error(body.message ?? `HTTP ${response.status}`);
    }
    return response.blob();
  },
  getAnalytics: (meetingId: string) => apiRequest(`/meetings/${meetingId}/analytics`),
  /** All meetings in the same recurring series (for Meeting History tab). */
  getSeriesInstances: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/series-instances`) as Promise<{ data?: { seriesId: string; meetings: unknown[] } }>,
  /** Batch fetch /details for every occurrence in a recurring series in one call. */
  getSeriesDetails: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/series-details`) as Promise<{
      data?: {
        meeting_id: string;
        instances: Array<{
          meeting_id: string;
          latest_recap?: { content?: unknown } | null;
          latest_analytics_rollup?: { content?: unknown } | null;
          analytics?: Record<string, unknown> | null;
          delivery_status_summary?: unknown;
        }>;
      };
    }>,
  /** Start next occurrence of a recurring meeting (creates new meeting row, returns newMeetingId). */
  startNextOccurrence: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/start-next-occurrence`, { method: 'POST' }) as Promise<{
      meetingId: string;
      joinUrl?: string;
      hostUrl?: string;
      password?: string;
      providerMeetingId?: string;
      meeting?: unknown;
    }>,
  /** Output IDs marked as finished for this meeting/series (for Outstanding vs Finished). */
  getClosedOutputs: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/outputs/closed`) as Promise<{
      data?: { closedOutputIds: string[]; closedOutputsWithTimestamp?: Record<string, number | null> };
      closedOutputIds?: string[];
      closedOutputsWithTimestamp?: Record<string, number | null>;
    }>,
  /** Mark an output as finished or outstanding. */
  updateOutputStatus: (meetingId: string, outputId: string, payload: { finished: boolean; sourceMeetingId?: string }) =>
    apiRequest(`/meetings/${meetingId}/outputs/${outputId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  /** Update output summary and/or owner. */
  updateOutputContent: (
    meetingId: string,
    outputId: string,
    payload: { summary?: string; owner?: string; sourceMeetingId?: string }
  ) =>
    apiRequest(`/meetings/${meetingId}/outputs/${outputId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  /** Email the meeting report to given address(es). */
  shareReport: (meetingId: string, body: { to: string | string[] }) =>
    apiRequest(`/meetings/${meetingId}/share-report`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Meeting prep — multipart upload (`file` field). scope aligns with list view (series vs this meeting). */
  uploadAttachment: (
    meetingId: string,
    file: File,
    options?: { scope?: 'series' | 'meeting'; shareWithAllParticipants?: boolean }
  ) => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('rithmo_token') : null;
    if (!token?.trim()) {
      authErrorHandler?.();
      return Promise.reject(
        Object.assign(new Error('Sign in to upload documents. Your session may have expired.'), {
          status: 401,
          code: 'UNAUTHORIZED',
        }) as Error & { status?: number; code?: string },
      );
    }
    const fd = new FormData();
    fd.append('file', file);
    if (options?.scope) {
      fd.append('scope', options.scope);
    }
    if (options?.shareWithAllParticipants) {
      fd.append('shareWithAllParticipants', 'true');
    }
    return apiRequestMultipart(`/meetings/${encodeURIComponent(meetingId)}/attachments`, fd);
  },
  /** Meeting prep — list materials (listScope: effective | meeting | series). */
  listAttachments: (meetingId: string, listScope: 'effective' | 'meeting' | 'series' = 'effective') =>
    apiRequest(
      `/meetings/${encodeURIComponent(meetingId)}/attachments?listScope=${encodeURIComponent(listScope)}`
    ),
  /** Soft-delete attachment (uploader or org admin). */
  deleteAttachment: (meetingId: string, attachmentId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/attachments/${encodeURIComponent(attachmentId)}`, {
      method: 'DELETE',
    }),
  /** Suggested agenda links from meeting prep (no LLM at click time; server uses stored prep data when available). */
  listAttachmentAgendaLinks: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/attachment-agenda-links`),
  patchAttachmentAgendaLink: (
    meetingId: string,
    linkId: string,
    body: { linkType: 'confirmed' | 'rejected' }
  ) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/attachment-agenda-links/${encodeURIComponent(linkId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  postAgendaSuggestionsFromAttachments: (
    meetingId: string,
    body: { listScope?: 'effective' | 'meeting' | 'series' } = {}
  ) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/agenda/suggestions/from-attachments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  postAgendaSuggestionsApply: (meetingId: string, linkIds: string[]) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/agenda/suggestions/apply`, {
      method: 'POST',
      body: JSON.stringify({ linkIds }),
    }),
  /** Chat with Rithmo — assistant_external dashboard chat. */
  chatWithRithmo: (meetingId: string, message: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/chat-with-rithmo`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  /** Per-meeting transcript Q&A for the meeting-page search bar. Scoped to this one meeting. */
  askMeeting: (meetingId: string, question: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    }) as Promise<{
      success: boolean
      data?: {
        format: 'short' | 'list' | 'prose' | 'quote' | 'none'
        answer: string
        bullets: string[]
        quotes: Array<{ speaker: string | null; text: string }>
        sources: Array<{ speaker: string | null; text: string; segmentStartId: string | null }>
        notFound: boolean
      }
    }>,
  updateScreenshareStatus: (meetingId: string, attachmentId: string, status: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/attachments/${encodeURIComponent(attachmentId)}/screenshare-status`, {
      method: 'PUT',
      body: JSON.stringify({ screenshare_status: status }),
    }),
  listDocumentMappings: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/document-mappings`),
  upsertDocumentMapping: (meetingId: string, data: object) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/document-mappings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteDocumentMapping: (meetingId: string, mappingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/document-mappings/${encodeURIComponent(mappingId)}`, {
      method: 'DELETE',
    }),
  /** Pre-meeting intelligence: briefing, risk assessment, topic continuity, agenda recs. */
  // Viewer-private post-meeting coaching for the Recap tab: tips, personal scorecard,
  // communication style, and tone breakdown. Scoped entirely to the authenticated user.
  getRecapPersonal: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/recap-personal`) as Promise<{
      data?: {
        tips: string[];
        coaching: { wentWell: string[]; toImprove: string[]; progress: string[] } | null;
        scorecard: {
          talkShare: { value: number; unit: string; trend: string; direction: string } | null;
          contributions: { value: number; unit: string; trend: string; direction: string } | null;
          attentiveness: { value: number; unit: string; trend: string; direction: string } | null;
          priorMeetingCount: number;
        } | null;
        communicationStyle: { style: string; engagement: string | null } | null;
        tone: {
          breakdown: Array<{ status: string; durationMs: number; pct: number }>;
          currentStatus: string | null;
          redFlag: boolean;
        } | null;
      };
    }>,

  getPreMeetingIntelligence: (meetingId: string, teamId?: string) =>
    apiRequest(
      `/meetings/${meetingId}/pre-meeting-intelligence${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ''}`,
    ) as Promise<{
      data?: {
        sourceMeetingId: string;
        teamId: string | null;
        computedAt: string;
        preMeetingBriefing: {
          team_snapshot?: string;
          key_risks?: string[];
          suggested_focus_areas?: string[];
          participant_notes?: Array<{
            participant_id: string;
            display_name: string | null;
            readiness_note: string;
            relationship_context: string | null;
          }>;
          follow_up_from_last_meeting?: string[];
        } | null;
        meetingRiskAssessment: {
          overall_risk_level: 'low' | 'medium' | 'high';
          risks: Array<{
            category: string;
            severity: 'low' | 'medium' | 'high';
            description: string;
            mitigation_suggestion: string;
          }>;
        } | null;
        topicContinuity: {
          open_loops: Array<{
            topic: string;
            status: 'unresolved' | 'deferred' | 'blocked';
            suggested_next_action: string;
          }>;
          resolved_topics: string[];
        } | null;
        agendaRecommendations: Array<{
          suggestedItemTitle: string;
          rationale: string;
          recommendedDurationMin: number | null;
          priority: 'low' | 'medium' | 'high';
        }>;
        participantSummaries: Array<{
          participantId: string;
          displayName: string | null;
          engagementStrategy: string | null;
          coachingNotes: string[];
        }>;
      };
    }>,
};

/** Dual-agenda internal goals (auth + internal-agenda gate on server). */
export const internalAgendaAPI = {
  get: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(String(meetingId))}/internal-agenda`),
  put: (meetingId: string, body: Record<string, unknown>) =>
    apiRequest(`/meetings/${encodeURIComponent(String(meetingId))}/internal-agenda`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

/**
 * Dashboard API - returns upcoming, draft, and recent (ended) meetings
 */
export const dashboardAPI = {
  /** Natural-language search over the user's meeting history (transcripts + signals). */
  search: (
    query: string,
    opts?: { dateFrom?: string; dateTo?: string; context?: { query: string; answer: string } | null },
  ) =>
    apiRequest('/dashboard/search', {
      method: 'POST',
      body: JSON.stringify({
        query,
        dateFrom: opts?.dateFrom,
        dateTo: opts?.dateTo,
        context: opts?.context ?? undefined,
      }),
    }) as Promise<{
      data?: {
        query: string
        answer: string
        intent: { governed: 'commitment' | 'strategy' | null; superlative: 'first' | 'last' | null; entities: string[]; analytical: boolean; metric: string | null; metricAggregate: 'mean' | 'total' | null; timeframe: string | null }
        sources: Array<{
          kind: 'commitment' | 'strategy' | 'transcript'
          text: string
          meetingId: string
          meetingTitle: string | null
          dateIso: string | null
          speaker: string | null
          segmentStartId: string | null
          segmentEndId: string | null
          cited: boolean
        }>
        noEvidence: boolean
        suggestions: Array<{
          kind: 'commitment' | 'strategy'
          summary: string
          evidenceQuote: string
          meetingId: string
          meetingTitle: string | null
          dateIso: string | null
          segmentStartId: string | null
          segmentEndId: string | null
          confidence: number
        }>
        aggregation?: {
          totalMatches: number
          distinctMeetings: number
          byMonth: Array<{ month: string; meetings: number; mentions: number }>
        } | null
        metric?: {
          key: string
          label: string
          unit: string
          aggregate: 'mean' | 'total'
          value: number | null
          meetingCount: number
          trendDelta: number | null
          byMonth: Array<{ month: string; value: number }>
          timeframePhrase: string
        } | null
        commitments?: Array<{
          outputId: string
          type: string
          summary: string
          owner: string | null
          dueDateText: string | null
          dueDateIso: string | null
          dueAtMs: number | null
          isOverdue: boolean
          meetingId: string
          meetingTitle: string | null
          dateIso: string | null
        }> | null
        teamOverview?: {
          role: string | null
          teamCount: number
          meetingCount: number
          decisions: number
          actionItems: number
          agendaAdherencePct: number | null
          talkBalancePct: number | null
          totalMinutes: number
        } | null
      }
    }>,
  /** Lightweight: one-time Mux video after the user’s first dashboard-built meeting (not post-completion). */
  getFirstMeetingCreatedVideo: () =>
    apiRequest('/dashboard/first-meeting-created-video') as Promise<{
      data?: { showFirstMeetingCreatedVideo: boolean };
      showFirstMeetingCreatedVideo?: boolean;
    }>,
  ackFirstMeetingCreatedVideo: () =>
    apiRequest('/dashboard/first-meeting-created-video/ack', { method: 'POST' }) as Promise<{
      data?: { ok: boolean };
      ok?: boolean;
    }>,
  /** Lightweight: whether to show the one-time post–first-meeting Mux video (for polling). */
  getPostFirstMeetingVideo: () =>
    apiRequest('/dashboard/post-first-meeting-video') as Promise<{
      data?: { showPostFirstMeetingVideo: boolean };
      showPostFirstMeetingVideo?: boolean;
    }>,
  ackPostFirstMeetingVideo: () =>
    apiRequest('/dashboard/post-first-meeting-video/ack', { method: 'POST' }) as Promise<{
      data?: { ok: boolean };
      ok?: boolean;
    }>,
  get: () =>
    apiRequest('/dashboard') as Promise<{
      upcomingMeetings: Array<{
        id: string;
        meetingId: string;
        title: string;
        description: string;
        status: string;
        startTime?: string | null;
        participantCount?: number;
        agendaCount?: number;
        ownerId?: string;
        /** True when the user is organizer or a participant (not only org/team visibility). */
        imInvolvedInMeeting?: boolean;
      }>;
      draftMeetings: Array<{
        id: string;
        meetingId: string;
        title: string;
        description: string;
        startTime?: string | null;
        lastUpdatedAtMs?: number;
        participantCount?: number;
        agendaCount?: number;
        imInvolvedInMeeting?: boolean;
      }>;
      recentMeetings: Array<{
        id: string;
        meetingId: string;
        title: string;
        startTime?: string | null;
        endedAtMs?: number | null;
      }>;
      /** True after the user’s first non-draft owned meeting exists (server + acknowledgement gate). */
      showFirstMeetingCreatedVideo?: boolean;
      /** True when the user should see the one-time welcome video (server + acknowledgement gate). */
      showPostFirstMeetingVideo?: boolean;
    }>,
  getMeetingConflicts: (start: string, end: string, excludeMeetingId?: string) =>
    apiRequest(
      `/dashboard/meeting-conflicts?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${excludeMeetingId ? `&excludeMeetingId=${encodeURIComponent(excludeMeetingId)}` : ''}`,
    ) as Promise<{
      data?: {
        meetings: Array<{
          meetingId: string;
          title: string;
          scheduledStartTime: string;
          scheduledEndTime: string;
          isRecurring?: boolean;
          recurrenceRule?: Record<string, unknown>;
          parentMeetingId?: string;
        }>;
      };
      meetings?: Array<{
        meetingId: string;
        title: string;
        scheduledStartTime: string;
        scheduledEndTime: string;
        isRecurring?: boolean;
        recurrenceRule?: Record<string, unknown>;
        parentMeetingId?: string;
      }>;
    }>,
  getCalendarMeetings: (from: string, to: string) =>
    apiRequest(
      `/dashboard/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ) as Promise<{
      data?: {
        meetings: Array<{
          id: string;
          meetingId: string;
          title: string;
          description?: string;
          status: string;
          startTime?: string | null;
          scheduledStartTime?: string | null;
          endTime?: string | null;
          endedAtMs?: number | null;
          isDraft?: boolean;
          participantCount?: number;
          agendaCount?: number;
          isRecurring?: boolean;
          recurringSeriesId?: string;
          recurrenceRule?: Record<string, unknown>;
          parentMeetingId?: string;
        }>;
      };
      meetings?: Array<Record<string, unknown>>;
    }>,
  /** Admin visibility only: meetings you can open but are not organiser / invitee (see intro copy). */
  getOversightMeetings: (limit?: number) =>
    apiRequest(`/dashboard/oversight-meetings${limit != null ? `?limit=${encodeURIComponent(String(limit))}` : ''}`) as Promise<{
      data?: {
        meetings: Array<Record<string, unknown>>;
        intro?: string;
      };
      meetings?: Array<Record<string, unknown>>;
      intro?: string;
    }>,
  getActiveMeeting: () =>
    apiRequest('/dashboard/active-meeting') as Promise<{
      data?: { activeMeeting: { id: string; title: string; status: string } | null };
      activeMeeting?: { id: string; title: string; status: string } | null;
    }>,
  /** Cross-meeting open commitments (uncompleted action items / follow-ups) for the Open Commitments widget. */
  getOpenCommitments: (limit?: number) =>
    apiRequest(`/dashboard/open-commitments${limit != null ? `?limit=${encodeURIComponent(String(limit))}` : ''}`) as Promise<{
      data?: {
        commitments: Array<{
          outputId: string;
          commitmentId: string | null;
          type: string;
          summary: string;
          owner: string | null;
          dueDate: string | null;
          dueAtMs: number | null;
          isOverdue: boolean;
          agendaItem: string | null;
          meetingId: string;
          meetingTitle: string;
          meetingEndedAtMs: number | null;
          sourceParty: 'internal' | 'external' | 'unknown' | null;
          attributionConfidence: number | null;
          needsReview: boolean;
        }>;
        totalOpen: number;
        overdueCount: number;
      };
    }>,
  /** Decision alignment report (decided vs built) for the Alignment widget. */
  getAlignmentReport: () =>
    apiRequest(`/dashboard/alignment`) as Promise<{
      data?: {
        enabled: boolean;
        report: {
          windowStartMs: number;
          windowEndMs: number;
          prsShipped: number;
          deliveredDecisions: number;
          discoveredDecisions: number;
          awaiting: number;
          governanceGaps: number;
          drift: number;
          unplannedPrs: number;
          undeliveredDecisions: number;
          alignmentPct: number;
          deliveredList: Array<{ title: string; prCount: number }>;
          discoveredList: Array<{ title: string }>;
          awaitingList: Array<{ title: string; status: string; owner: string | null }>;
          gapList: Array<{ title: string }>;
          driftList: Array<{ title: string }>;
          undeliveredList: Array<{ title: string }>;
        } | null;
      };
    }>,
  /** Strategic decisions with their evolution events, for the Strategy tracker timeline. */
  getStrategy: (limit?: number) =>
    apiRequest(`/dashboard/strategy${limit != null ? `?limit=${encodeURIComponent(String(limit))}` : ''}`) as Promise<{
      data?: {
        strategies: Array<{
          id: string;
          summary: string;
          theme: string | null;
          polarity: 'affirmative' | 'negative';
          weight: number | null;
          tier: 'strategy' | 'operational';
          status: 'active' | 'evolving' | 'superseded' | 'reversed' | 'realized';
          supersedesId: string | null;
          decidedAtMs: number | null;
          sourceMeetingId: string;
          execution: { total: number; done: number } | null;
          sourceParty: 'internal' | 'external' | 'unknown' | null;
          attributionConfidence: number | null;
          needsReview: boolean;
          events: Array<{
            kind: string;
            source: string;
            confidence: number | null;
            meetingId: string | null;
            relatedStrategyId: string | null;
            summary: string | null;
            occurredAtMs: number | null;
            createdAt: string;
          }>;
        }>;
        totalActive: number;
      };
    }>,
  /** Synthesized strategy threads (molecules) for the thread-centric Strategy view. */
  getStrategyThreads: () =>
    apiRequest(`/dashboard/strategy/threads`) as Promise<{
      data?: {
        enabled: boolean;
        threads: Array<{
          id: string;
          title: string;
          thesis: string | null;
          theme: string | null;
          status: string;
          momentum: string | null;
          soundness: number | null;
          soundnessRationale: string | null;
          dimensionScores: Record<string, number> | null;
          evidenceConfidence: number | null;
          frame: {
            objective?: string | null;
            bets?: string[];
            boundaries?: string[];
            metrics?: string[];
            owners?: string[];
            horizon?: string | null;
            dependencies?: string[];
            risks?: string[];
            summary?: string;
          } | null;
          lastMovementAtMs: number | null;
          generatedAt: string;
          meetingWindow: { atomCount?: number; meetingCount?: number; firstAtMs?: number; lastAtMs?: number } | null;
          origin: string | null;
          level?: string;
          teamId?: string | null;
          teamName?: string | null;
          gaps: Array<{
            id: string;
            kind: string;
            description: string;
            severity: 'info' | 'warn' | 'critical';
            status: string;
            spawnedCommitmentId?: string | null;
            routedTo?: string | null;
            draftable?: boolean;
            draft?: {
              id: string;
              slot: string;
              draftText: string;
              rationale: string | null;
              status: 'proposed' | 'accepted' | 'dismissed';
            } | null;
          }>;
          members: Array<{
            atomId: string;
            role: string;
            summary: string;
            status: string;
            theme: string | null;
            decidedAtMs: number | null;
            sourceMeetingId: string | null;
          }>;
          execution: { total: number; open: number; overdue: number; done: number };
          assessmentHistory: Array<{ generatedAt: string; soundness: number | null; momentum: string | null; status: string | null }>;
        }>;
        portfolio: {
          threadCount: number;
          byStatus: Record<string, number>;
          byMomentum: Record<string, number>;
          openGapCount: number;
          criticalGapCount: number;
          lowConfidenceCount: number;
          meanSoundness: number | null;
        } | null;
        synthesisState: { lastSynthesizedAt: string | null; processing: boolean; dirty: boolean } | null;
      };
    }>,
  /** Trigger on-demand re-synthesis of the org's strategy threads (Phase 3). */
  reanalyzeStrategy: () =>
    apiRequest(`/dashboard/strategy/threads/reanalyze`, { method: 'POST' }) as Promise<{
      data?: { enabled: boolean; queued: boolean; orgId?: string };
    }>,
  /** Draft a concrete fill for one strategy gap (Phase 6 formulation assist). */
  draftStrategyGapFill: (gapId: string) =>
    apiRequest(`/dashboard/strategy/gaps/${gapId}/draft`, { method: 'POST' }) as Promise<{
      data?: {
        draft: {
          id: string;
          slot: string;
          draftText: string;
          rationale: string;
          status: 'proposed';
        };
      };
    }>,
  /** Accept (optionally edited) or dismiss a drafted gap fill (Phase 6). */
  decideStrategyGapDraft: (draftId: string, decision: 'accepted' | 'dismissed', editedText?: string) =>
    apiRequest(`/dashboard/strategy/drafts/${draftId}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, editedText }),
    }) as Promise<{ data?: { status: 'accepted' | 'dismissed' } }>,
  /** Declared strategy + intent↔reality delta (Phase 3.5). */
  getStrategyIntents: () =>
    apiRequest(`/dashboard/strategy/intents`) as Promise<{
      data?: {
        enabled: boolean;
        canDeclare: boolean;
        intents: Array<{
          id: string;
          title: string;
          thesis: string;
          alignment: 'aligned' | 'uncascaded';
          matchedThreadId: string | null;
          matchedThreadTitle: string | null;
          similarity: number | null;
          createdAt: string;
        }>;
        undeclared: Array<{ id: string; title: string; thesis: string | null; status: string; teamName?: string | null }>;
        summary: {
          declaredCount: number;
          alignedCount: number;
          uncascadedCount: number;
          undeclaredCount: number;
        } | null;
      };
    }>,
  /** Declare an org strategy (org_admin only, Phase 3.5). */
  declareStrategyIntent: (body: { title: string; thesis: string }) =>
    apiRequest(`/dashboard/strategy/intents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ data?: { intentId: string } }>,
  /** Archive a declared intent (org_admin only, Phase 3.5). */
  archiveStrategyIntent: (intentId: string) =>
    apiRequest(`/dashboard/strategy/intents/${intentId}/archive`, { method: 'POST' }) as Promise<{
      data?: { archived: boolean };
    }>,
  /** Edit a strategy's wording and/or verify a flagged one as the org's own. */
  updateStrategy: (strategyId: string, body: { summary?: string; verified?: boolean }) =>
    apiRequest(`/dashboard/strategy/${strategyId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{ data?: { strategyId: string; updated: boolean } }>,
  /** Delete a wrongly-captured strategy (e.g. an external participant's own plan). */
  deleteStrategy: (strategyId: string) =>
    apiRequest(`/dashboard/strategy/${strategyId}`, { method: 'DELETE' }) as Promise<{
      data?: { strategyId: string; deleted: boolean };
    }>,
  /** Edit a commitment's wording and/or verify a flagged one as the org's own. */
  updateCommitment: (commitmentId: string, body: { summary?: string; verified?: boolean }) =>
    apiRequest(`/dashboard/commitments/${commitmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{ data?: { commitmentId: string; updated: boolean } }>,
  /** Delete a wrongly-captured commitment. */
  deleteCommitment: (commitmentId: string) =>
    apiRequest(`/dashboard/commitments/${commitmentId}`, { method: 'DELETE' }) as Promise<{
      data?: { commitmentId: string; deleted: boolean };
    }>,
};

export interface ChatSource {
  kind: 'commitment' | 'strategy' | 'transcript'
  text: string
  meetingId: string
  meetingTitle: string | null
  dateIso: string | null
  speaker: string | null
  cited: boolean
}

export interface ChatTurn {
  id: string
  conversationId: string
  seq: number
  role: 'user' | 'assistant'
  content: string
  sources: ChatSource[] | null
  noEvidence: boolean
  viaAgent: boolean
  model: string | null
  createdAt: string
}

export interface ChatConversationSummary {
  id: string
  title: string | null
  createdAt: string
  updatedAt: string
  lastMessageAt: string | null
}

/** Persistent, multi-turn chat over the user's meeting history (SEARCH_CHAT_ENABLED). */
export const chatAPI = {
  /** Send a message; starts a thread when conversationId is omitted. */
  send: (message: string, conversationId?: string | null) =>
    apiRequest('/dashboard/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationId: conversationId ?? undefined }),
    }) as Promise<{
      data?: {
        conversationId: string
        title: string | null
        userTurn: ChatTurn | null
        assistantTurn: ChatTurn
      }
    }>,
  /** The user's saved threads, most-recent first. */
  listConversations: () =>
    apiRequest('/dashboard/chat/conversations') as Promise<{
      data?: { conversations: ChatConversationSummary[] }
    }>,
  /** One thread with its ordered turns. */
  getConversation: (id: string) =>
    apiRequest(`/dashboard/chat/conversations/${encodeURIComponent(id)}`) as Promise<{
      data?: { conversation: ChatConversationSummary; turns: ChatTurn[] }
    }>,
  /** Soft-delete a thread. */
  deleteConversation: (id: string) =>
    apiRequest(`/dashboard/chat/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }) as Promise<{
      data?: { deleted: boolean }
    }>,
}

type SetupDraftStatus = 'draft' | 'completed' | 'abandoned';
type SetupDraftRole = 'assistant' | 'user';

interface SetupDraftMessage {
  id: string;
  role: SetupDraftRole;
  content: string;
  createdAtMs: number;
}

interface SetupDraftPayload {
  meetingId: string;
  status: SetupDraftStatus;
  messages: SetupDraftMessage[];
  extractedProfile: Record<string, unknown>;
  activeThreadId?: string | null;
  chatThreads?: SetupChatThreadSummary[];
}

interface SetupChatThreadSummary {
  threadId: string;
  title: string;
  createdAtMs: number;
  updatedAtMs: number;
  lastMessageAtMs: number;
  messageCount: number;
}

interface SetupTurnPayload {
  draft: SetupDraftPayload;
  assistantMessage: string;
  extractedProfile: Record<string, unknown>;
  fallbackUsed: boolean;
  turn: {
    persona: 'executiveDelegator' | 'managerOperator' | 'adminHybrid' | 'explorer' | null;
    personaConfidence: number;
    missingRequiredFields: string[];
    clarificationNeeded: boolean;
    clarificationReason: string | null;
    recommendedNextStep: string | null;
    readyForSummary: boolean;
    structuredUiCard: 'none' | 'invite' | 'zoomConnect' | 'summaryApproval' | 'meetingCreate';
  };
}

interface SetupCompletionPayload {
  draft: SetupDraftPayload;
  nextStep: {
    code: string;
    label: string;
    path: string;
  };
  mapping: {
    persistedFields: string[];
    deferredFields: string[];
    notes: string[];
  };
}

interface SetupNextActionItem {
  id: string;
  type: 'delegateInvite' | 'connectZoom' | 'openQuickStart' | 'openDashboard';
  enabled: boolean;
  label: string;
  destination?: string;
  connected?: boolean;
}

interface SetupNextActionsPayload {
  actions: SetupNextActionItem[];
  persona: 'executiveDelegator' | 'managerOperator' | 'adminHybrid' | 'explorer' | null;
  setupCompleted: boolean;
  zoomConnected: boolean;
}

export const setupAPI = {
  getDraft: () =>
    apiRequest('/setup/draft') as Promise<{
      data?: { draft: SetupDraftPayload };
      draft?: SetupDraftPayload;
    }>,
  postMessage: (content: string) =>
    apiRequest('/setup/draft/messages', {
      method: 'POST',
      body: JSON.stringify({ content }),
    }) as Promise<{
      data?: { draft: SetupDraftPayload };
      draft?: SetupDraftPayload;
    }>,
  listChats: () =>
    apiRequest('/setup/chats') as Promise<{
      data?: { chats: SetupChatThreadSummary[]; activeThreadId: string | null };
      chats?: SetupChatThreadSummary[];
      activeThreadId?: string | null;
    }>,
  createChat: (title?: string) =>
    apiRequest('/setup/chats', {
      method: 'POST',
      body: JSON.stringify(title ? { title } : {}),
    }) as Promise<{
      data?: { threadId: string; chats: SetupChatThreadSummary[]; draft: SetupDraftPayload };
      threadId?: string;
      chats?: SetupChatThreadSummary[];
      draft?: SetupDraftPayload;
    }>,
  getChat: (threadId: string) =>
    apiRequest(`/setup/chats/${encodeURIComponent(threadId)}`) as Promise<{
      data?: { thread: SetupChatThreadSummary; messages: SetupDraftMessage[]; draft: SetupDraftPayload };
      thread?: SetupChatThreadSummary;
      messages?: SetupDraftMessage[];
      draft?: SetupDraftPayload;
    }>,
  deleteChat: (threadId: string, body?: { activeThreadId?: string }) =>
    apiRequest(`/setup/chats/${encodeURIComponent(threadId)}`, {
      method: 'DELETE',
      body: JSON.stringify(body ?? {}),
    }) as Promise<{
      data?: {
        deletedThreadId: string;
        chats: SetupChatThreadSummary[];
        activeThreadId: string | null;
        messages?: SetupDraftMessage[];
        draft: SetupDraftPayload;
      };
      deletedThreadId?: string;
      chats?: SetupChatThreadSummary[];
      activeThreadId?: string | null;
      messages?: SetupDraftMessage[];
      draft?: SetupDraftPayload;
    }>,
  postTurn: (content: string, threadId?: string) =>
    apiRequest('/setup/turn', {
      method: 'POST',
      body: JSON.stringify(threadId ? { content, threadId } : { content }),
    }) as Promise<{
      data?: SetupTurnPayload;
      draft?: SetupDraftPayload;
      assistantMessage?: string;
      extractedProfile?: Record<string, unknown>;
      fallbackUsed?: boolean;
      turn?: SetupTurnPayload['turn'];
    }>,
  complete: (body: { approved: true } = { approved: true }) =>
    apiRequest('/setup/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      data?: SetupCompletionPayload;
      draft?: SetupDraftPayload;
      nextStep?: SetupCompletionPayload['nextStep'];
      mapping?: SetupCompletionPayload['mapping'];
    }>,
  getNextActions: () =>
    apiRequest('/setup/next-actions') as Promise<{
      data?: SetupNextActionsPayload;
      actions?: SetupNextActionItem[];
      persona?: SetupNextActionsPayload['persona'];
      setupCompleted?: boolean;
      zoomConnected?: boolean;
    }>,
  postDelegateInvite: (email: string) =>
    apiRequest('/setup/delegate-invite', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }) as Promise<{
      data?: {
        delegated: boolean;
        invite: { email: string; status: string };
      };
      delegated?: boolean;
      invite?: { email: string; status: string };
    }>,
  patchDraft: (body: { status?: SetupDraftStatus; extractedProfile?: Record<string, unknown> }) =>
    apiRequest('/setup/draft', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{
      data?: { draft: SetupDraftPayload };
      draft?: SetupDraftPayload;
    }>,
};

// =============================================================================
// SETUP BEHAVIOR INSPECTOR (read-only, dev/admin diagnostic)
// =============================================================================

/** Single behavior status enum, mirrored from backend. */
export type SetupBehaviorStatus =
  | 'active'
  | 'stored_not_consumed'
  | 'captured_future_only'
  | 'not_configured'
  | 'not_wired'
  | 'not_found';

export interface SetupBehaviorRow {
  capturedPreference: unknown | null;
  mappedSetting: unknown | null;
  persistedValue: unknown | null;
  actualRuntimeSource: string;
  expectedMeetingBehavior: string;
  status: SetupBehaviorStatus;
}

export interface SetupEffectiveBehaviorRow {
  source:
    | 'meeting_type_default'
    | 'team_override'
    | 'meeting_override'
    | 'live_override'
    | 'users_timezone'
    | 'governance_defaults'
    | 'hardcoded'
    | 'none';
  value: unknown;
  status: SetupBehaviorStatus;
}

export interface SetupBehaviorDiffEntry {
  area: string;
  intendedValue: unknown;
  actualValue: unknown;
  reason: string;
}

export interface SetupMappedPolicyEntry {
  key: string;
  value: unknown;
  sourceIntents: string[];
  source: 'structured_mapping' | 'legacy_heuristic' | 'fallback_extraction';
  supportedToday: boolean;
  consumedToday: boolean;
}

export interface SetupBehaviorInspectorReport {
  generatedAtIso: string;
  flag: 'SETUP_BEHAVIOR_INSPECTOR_ENABLED';
  user: {
    userId: string;
    email: string | null;
    fullName: string | null;
    timezone: string | null;
    zoomConnected: boolean;
    primaryOrgId: string | null;
    role: string | null;
  };
  setupSource: {
    status: 'draft' | 'completed' | 'abandoned' | 'none';
    setupAnchorMeetingId: string | null;
    extractedProfile: Record<string, unknown> | null;
    chatThreadIds: string[];
    messageCount: number;
    lastSetupMessageAtMs: number | null;
    completedAtMs: number | null;
  };
  capturedIntent: {
    persona: string | null;
    branch: string | null;
    desiredOutcomes: string[];
    meetingPainPoints: string[];
    operatingPrinciples: string[];
    interventionPreferences: string[];
    directnessPreference: string | null;
    inferredTone: string | null;
    initialRolloutScope: string | null;
    delegationIntent: unknown | null;
    timezone: string | null;
    /**
     * Phase 3A read-only visibility. Closed-vocab tokens captured by setup
     * chat. Older backends may omit these fields, so mark them optional.
     */
    urgencyPreference?: string | null;
    requestedIntensity?: string | null;
    futurePreferences: string[];
    confidence: number | null;
  };
  mappingReplay: {
    governanceDefaults: Record<string, unknown> | null;
    userProfilePatch: { timezone?: string };
    persistedFields: string[];
    deferredFields: string[];
    appliedPolicies: SetupMappedPolicyEntry[];
    futurePreferences: string[];
    fallbackExtractionApplied: boolean;
    fallbackDetectedSignals: string[];
    notes: string[];
  };
  persistedSettings: {
    users: { timezone: string | null };
    governanceDefaults: Record<string, unknown> | null;
    notificationSettings: {
      status: 'not_found';
      message: string;
      detectedAccountSettings: string[];
    };
  };
  behaviorProjection: {
    agendaEnforcement: SetupBehaviorRow;
    talkEquity: SetupBehaviorRow;
    interruptionPrevention: SetupBehaviorRow;
    timezoneScheduling: SetupBehaviorRow;
    notificationsAndNudges: SetupBehaviorRow;
    quickStartHandoff: SetupBehaviorRow;
  };
  effectiveBehavior: {
    agendaEnforcement: SetupEffectiveBehaviorRow;
    talkEquity: SetupEffectiveBehaviorRow;
    interruptionPrevention: SetupEffectiveBehaviorRow;
    timezoneScheduling: SetupEffectiveBehaviorRow;
    notificationsAndNudges: SetupEffectiveBehaviorRow;
    quickStartHandoff: SetupEffectiveBehaviorRow;
  };
  behaviorDiff: SetupBehaviorDiffEntry[];
  policyResolver: {
    resolverPath: string;
    overrideLayers: string[];
    userLevelOverrideExists: boolean;
    setupDerivedSettingsAffectResolver: boolean;
    evidenceFiles: string[];
  };
  summary: string;
  warnings: string[];
  /** Optional categorized warnings (added in Phase 1 cleanup). May be absent on older backends. */
  classifiedWarnings?: Array<{ category: string; message: string }>;
}

export const setupBehaviorAPI = {
  /**
   * Fetch the Customer Behavior Plan Inspector payload for a user.
   *
   * Optional `windowDays` (7 | 30 | 90 | 'all', default 30) and `limit`
   * (1..500, default 200) scope the historical timeline. Anything else
   * returns 400 from the backend; the page swallows that into the existing
   * error toast.
   */
  get: (
    userId: string | number,
    options: { windowDays?: 7 | 30 | 90 | 'all'; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (options.windowDays !== undefined) params.set('windowDays', String(options.windowDays));
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    const qs = params.toString();
    const path = `/admin/setup-behavior/${encodeURIComponent(String(userId))}${qs ? `?${qs}` : ''}`;
    return apiRequest(path) as Promise<{
      data?: SetupBehaviorInspectorReport;
      // Defensive shape: some endpoints expose the payload at the top level.
      [K: string]: unknown;
    }>;
  },
};

export const agendaAPI = {
  init: (meetingId: string, data: object) =>
    apiRequest(`/agenda/${meetingId}/init`, { method: 'POST', body: JSON.stringify(data) }),
  setItems: (meetingId: string, items: unknown[]) =>
    apiRequest(`/agenda/${meetingId}/items`, { method: 'POST', body: JSON.stringify({ items }) }),
  start: (meetingId: string) => apiRequest(`/agenda/${meetingId}/start`, { method: 'POST' }),
  advance: (meetingId: string) => apiRequest(`/agenda/${meetingId}/advance`, { method: 'POST' }),
  skip: (meetingId: string, reason?: string) =>
    apiRequest(`/agenda/${meetingId}/skip`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getStatus: (meetingId: string) => apiRequest(`/agenda/${meetingId}/status`),
  processUtterance: (meetingId: string, data: object) =>
    apiRequest(`/agenda/${meetingId}/utterance`, { method: 'POST', body: JSON.stringify(data) }),
  get: (meetingId: string) => apiRequest(`/meetings/${meetingId}/agenda`),
  update: (meetingId: string, agenda: unknown) =>
    apiRequest(`/meetings/${meetingId}/agenda`, { method: 'PUT', body: JSON.stringify({ agenda }) }),
  addItem: (meetingId: string, item: object) =>
    apiRequest(`/meetings/${meetingId}/agenda/items`, { method: 'POST', body: JSON.stringify(item) }),
  updateItem: (meetingId: string, itemId: string, data: object) =>
    apiRequest(`/meetings/${meetingId}/agenda/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteItem: (meetingId: string, itemId: string) =>
    apiRequest(`/meetings/${meetingId}/agenda/items/${itemId}`, { method: 'DELETE' }),
  reorderItems: (meetingId: string, itemIds: string[]) =>
    apiRequest(`/meetings/${meetingId}/agenda/reorder`, { method: 'POST', body: JSON.stringify({ itemIds }) }),
  revertLlmAgenda: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/agenda/revert-llm`, { method: 'POST' }),

  // Phase 3 Agenda Composer V2 endpoints
  getAgendaV2: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/agenda/v2`),
  rejectAgendaItem: (meetingId: string, itemId: string | number, body: { scope: 'this_cycle' | 'suppress'; cycles?: number; reason?: string }) =>
    apiRequest(`/meetings/${meetingId}/agenda/items/${itemId}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  regenerateAgendaEnrichment: (meetingId: string, itemId: string | number) =>
    apiRequest(`/meetings/${meetingId}/agenda/items/${itemId}/regenerate-enrichment`, {
      method: 'POST',
    }),
  editAgendaItem: (meetingId: string, itemId: string | number, fields: Record<string, unknown>) =>
    apiRequest(`/meetings/${meetingId}/agenda/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),
  editAgendaEnrichment: (meetingId: string, itemId: string | number, fields: Record<string, unknown>) =>
    apiRequest(`/meetings/${meetingId}/agenda/items/${itemId}/enrichment`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),
};

export const agendaArchitectAPI = {
  /** Latest passed Architect run for a parent recurring meeting. 404 means no run yet. */
  getLatest: (parentMeetingId: string) =>
    apiRequest(`/agenda-architect/latest/${parentMeetingId}`),
  /** Record accept | edit | discard feedback for a specific architect run. */
  postFeedback: (
    runId: string,
    body: {
      verdict: 'accept' | 'edit' | 'discard';
      accepted_agenda?: unknown;
      diff?: unknown;
      notes?: string;
    },
  ) =>
    apiRequest(`/agenda-architect/feedback/${runId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// Phase 4 — Exec Pre-Read endpoints (organizer-only one-pager PDF).
export const preReadAPI = {
  /** Drives the button visibility check on mount. Returns { eligible, reason, message, signals, score, ... }. */
  getEligibility: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/pre-read/eligibility`),

  /** Enqueue generation. body { force?: boolean }. Returns 202 with { state, idempotencyKey, etaSec }. */
  generate: (meetingId: string, opts: { force?: boolean } = {}) =>
    apiRequest(`/meetings/${meetingId}/pre-read/generate`, {
      method: 'POST',
      body: JSON.stringify(opts),
    }),

  /** Polled by UI while non-terminal. Returns { state, progress, etaSec, atAGlance, canDownload, ... }. */
  getStatus: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/pre-read/status`),

  /**
   * Direct download URL for `<a href>`. Note: this won't include the Bearer token
   * header, so we use a fetch+blob path in the component below instead.
   */
  downloadUrl: (meetingId: string) =>
    `/api/meetings/${meetingId}/pre-read/download`,

  /**
   * In-app HTML reader URL. Returns text/html (screen-friendly render of the
   * brief), or 409 { code: 'needs_regenerate' } for legacy briefs. Fetched with
   * the Bearer token in the component (not a plain navigation).
   */
  viewUrl: (meetingId: string) =>
    `/api/meetings/${meetingId}/pre-read/view`,

  /** Clears the artifact + state. */
  deletePreRead: (meetingId: string) =>
    apiRequest(`/meetings/${meetingId}/pre-read`, { method: 'DELETE' }),
};

export const zoomAPI = {
  getState: (meetingId: string) => apiRequest(`/zoom/state/${meetingId}`),
  updateState: (meetingId: string, data: object) =>
    apiRequest(`/zoom/state/${meetingId}`, { method: 'POST', body: JSON.stringify(data) }),
  mute: (data: object) =>
    apiRequest('/zoom/mute', { method: 'POST', body: JSON.stringify(data) }),
  getModerationStatus: (meetingUUID?: string) =>
    apiRequest(`/zoom/moderation/status${meetingUUID ? `?meetingUUID=${meetingUUID}` : ''}`),
  testMute: (data: object) =>
    apiRequest('/zoom/moderation/test-mute', { method: 'POST', body: JSON.stringify(data) }),
  /** Check if Zoom is connected (after OAuth callback). Tokens stay server-side. */
  getOAuthCredentials: () => apiRequest('/zoom/oauth') as Promise<{ data?: { connected: boolean; email?: string } }>,
  /**
   * Create an instant meeting on the connected user's Zoom account (uses stored OAuth token).
   * Same response shape as cppBotAPI.createMeeting for compatibility.
   */
  createMeetingWithUserToken: (body: {
    topic?: string;
    agenda?: Array<{ id?: string; title: string; durationSec?: number }>;
    participants?: Array<{ id?: string; name?: string; email?: string }>;
    meetingType?: string;
    enableRecallBot?: boolean;
    recallTranscriptionOnly?: boolean;
    governanceSettings?: Record<string, unknown>;
  }) =>
    apiRequest('/zoom/meetings', { method: 'POST', body: JSON.stringify(body) }) as Promise<{
      success: boolean;
      createdWith?: 'user_oauth';
      meeting: { meetingId: string; meetingNumber?: string; joinUrl: string; hostUrl?: string; password?: string; topic?: string };
      cppBot?: { joined: boolean; isHost: boolean; addBotAfterYouJoin?: boolean };
      recallBot?: { id?: string; status?: string; joined?: boolean; error?: string };
    }>,
  /**
   * Start an already-created meeting with the C++ bot as host (before user joins).
   * Call when user clicks "Join meeting" so the bot starts the meeting, then user joins via join_url.
   * Body: { meetingId, password? }
   */
  startExistingMeetingForUser: (body: { meetingId: string; password?: string; force?: boolean }) =>
    apiRequest('/zoom/meetings/start-existing', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ success: boolean; message?: string }>,

  /**
   * Add C++ bot to a meeting created with user OAuth (call after user has joined the meeting).
   * Body: { meetingId, joinUrl, password? }
   */
  joinBotToMeeting: (body: { meetingId: string; joinUrl: string; password?: string }) =>
    apiRequest('/zoom/meetings/join-bot', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      success: boolean;
      message?: string;
      cppBot?: { joined: boolean };
    }>,
};

export const interruptAPI = {
  getStatus: (meetingId: string) => apiRequest(`/interrupt/${meetingId}/status`),
  getConfig: (meetingId: string) => apiRequest(`/interrupt/${meetingId}/config`),
  updateConfig: (meetingId: string, config: object) =>
    apiRequest(`/interrupt/${meetingId}/config`, { method: 'POST', body: JSON.stringify(config) }),
  getParticipantStats: (meetingId: string, participantId?: string) =>
    apiRequest(`/interrupt/${meetingId}/participants${participantId ? `/${participantId}` : ''}`),
  resetParticipant: (meetingId: string, participantId: string) =>
    apiRequest(`/interrupt/${meetingId}/participants/${participantId}/reset`, { method: 'POST' }),
  triggerMute: (meetingId: string) =>
    apiRequest(`/interrupt/${meetingId}/mute`, { method: 'POST' }),
};

export const calendarAPI = {
  listIntegrations: () => apiRequest('/integrations/calendars'),
  connectGoogle: () => apiRequest('/integrations/google/connect', { method: 'POST' }),
  connectMicrosoft: () => apiRequest('/integrations/microsoft/connect', { method: 'POST' }),
  disconnect: (provider: string) =>
    apiRequest(`/integrations/${provider}/disconnect`, { method: 'POST' }),
  sync: (provider: string) =>
    apiRequest(`/integrations/${provider}/sync`, { method: 'POST' }),
  listEvents: (provider: string, from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString();
    return apiRequest(`/integrations/${provider}/events${query ? `?${query}` : ''}`);
  },
  // Slack identity link (commitment DM check-ins). Connect when your Slack email
  // differs from your Rithmo email, so email-match can't map you automatically.
  slackStatus: () => apiRequest('/integrations/slack/status'),
  connectSlack: () => apiRequest('/integrations/slack/connect', { method: 'POST' }),
  githubStatus: () => apiRequest('/integrations/github/status'),
  connectGithub: () => apiRequest('/integrations/github/connect', { method: 'POST' }),
};

export const botAPI = {
  create: (opts: { meetingUrl: string; meetingPassword?: string; meetingUUID?: string }) =>
    apiRequest('/bot/create', { method: 'POST', body: JSON.stringify(opts) }),
  getStatus: (meetingId: string) => apiRequest(`/bot/status/${meetingId}`),
  createZoomBot: (opts: {
    meetingUrl: string;
    meetingPassword?: string;
    meetingUUID?: string;
    meetingJoinKind?: 'hosted' | 'assistant_external';
    botDisplayName?: string;
  }) =>
    apiRequest('/zoom/create-bot', { method: 'POST', body: JSON.stringify(opts) }),
  getZoomBotStatus: (meetingId: string) => apiRequest(`/zoom/bot-status/${meetingId}`),
};

export const cppBotAPI = {
  createMeeting: (options: {
    topic?: string;
    displayName?: string;
    agenda?: Array<{ id?: string; title: string; durationSec?: number }>;
    participants?: Array<{ id?: string; name?: string; email?: string }>;
    meetingType?: string;
    enableRecallBot?: boolean;
    governanceSettings?: Record<string, unknown>;
  } = {}) =>
    apiRequest('/cpp-bot/create-meeting', { method: 'POST', body: JSON.stringify(options) }),
  join: (options: object) =>
    apiRequest('/cpp-bot/join', { method: 'POST', body: JSON.stringify(options) }),
  leave: (meetingId?: string) =>
    apiRequest('/cpp-bot/leave', { method: 'POST', body: JSON.stringify({ meetingId }) }),
  endAll: (meetingId?: string) =>
    apiRequest('/cpp-bot/end-all', { method: 'POST', body: JSON.stringify({ meetingId }) }),
  getParticipants: (meetingId?: string) =>
    apiRequest(`/cpp-bot/participants${meetingId ? `?meetingId=${meetingId}` : ''}`),
  mute: (options: object) =>
    apiRequest('/cpp-bot/mute', { method: 'POST', body: JSON.stringify(options) }),
  unmute: (options: object) =>
    apiRequest('/cpp-bot/unmute', { method: 'POST', body: JSON.stringify(options) }),
  speak: (options: object) =>
    apiRequest('/cpp-bot/speak', { method: 'POST', body: JSON.stringify(options) }),
  health: () => apiRequest('/cpp-bot/health'),
  status: () => apiRequest('/cpp-bot/status'),
};

export const governanceAPI = {
  getSettings: (meetingId: string) => apiRequest(`/governance/${meetingId}/settings`),
  updateSettings: (meetingId: string, settings: object) =>
    apiRequest(`/governance/${meetingId}/settings`, { method: 'PATCH', body: JSON.stringify(settings) }),
  getDefaults: () => apiRequest('/governance/defaults'),
  updateDefaults: (settings: object) =>
    apiRequest('/governance/defaults', { method: 'PATCH', body: JSON.stringify(settings) }),
};

export const analyticsAPI = {
  /** Aggregated quick stats, trends, insights, recentMeetingAnalytics. period: 7d|30d|12m or week|month|year */
  getDashboard: (period = '7d', teamId?: string | null, parentMeetingId?: string | null, scope?: 'personal' | null, baseline?: boolean) => {
    const q = new URLSearchParams({ period: String(period) });
    if (teamId && String(teamId).trim()) q.set('teamId', String(teamId).trim());
    if (parentMeetingId && String(parentMeetingId).trim()) q.set('parentMeetingId', String(parentMeetingId).trim());
    if (scope === 'personal') q.set('scope', 'personal');
    if (baseline) q.set('baseline', 'true');
    return apiRequest(`/analytics/dashboard?${q.toString()}`) as Promise<{
      data?: {
        period: string;
        label: string;
        periodStart?: string;
        periodEnd?: string;
        /** Which meetings are included + how interruption/tangent totals work */
        visibility?: {
          scope: 'organization' | 'personal_visibility' | 'team';
          meetingsIncluded: string;
          metricsDefinition: string;
          teamId?: string;
          teamName?: string;
        };
        meetingCount: number;
        quick: {
          decisionsAvg: number;
          talkEquityAvg: number;
          interruptions: number;
          agendaAdherenceAvg: number;
          tangentsDetected?: number;
          trends: { decisions: string; talkEquity: string; interruptions: string; agenda: string };
        };
        insights: {
          decisionVelocity?: { value?: string; detail?: string } | null;
          agendaDrift?: { value?: string; detail?: string } | null;
          participationCoverage?: { value?: string; detail?: string } | null;
          actionFollowThrough?: { value?: string; detail?: string } | null;
        };
        avgDurationMinutes: number | null;
        productivity?: {
          index: number;
          decisionsPerMeeting: number;
          actionItemsClosedRate: number | null;
          meetingsImproved: number;
          interruptions: number;
        };
        efficiency?: {
          onTimeFinishRate: number;
          avgOverrunMinutes: number;
          avgDurationMinutes: number;
          focusTimeRatio: number;
          timeDisciplineScore?: number | null;
          score?: number;
        };
        decisionFollowThrough?: {
          decisionsPerMeeting: number;
          completed: number;
          pending: number;
          avgDaysToClose: number | null;
          rate: number;
        };
        timeSaved?: {
          earlyEndMinutes: number;
          interruptionTimeSavedMinutes?: number;
          tangentTimeSavedMinutes?: number;
          tangentsDivertedCount?: number;
          totalMinutes: number;
        };
        scorecardScores?: Record<string, number | null | undefined>;
        aggregatedParticipants?: Record<string, {
          displayName: string;
          totalTalkTimeMinutes: number;
          totalInterruptions: number;
          meetingCount: number;
        }>;
        recentMeetingAnalytics: Array<{
          meetingId: string;
          title?: string | null;
          endedAtMs?: number | null;
          startedAtMs?: number | null;
          scheduledStartTime?: string | null;
          scheduledEndTime?: string | null;
          earlyEndMinutes?: number | null;
          onTime?: boolean;
          totalMeetingMinutes?: number | null;
          talkEquityScore: number | null;
          agendaAdherence: number | null;
          agendaQualityScore?: number | null;
          agendaItemCount?: number | null;
          agendaItemCompleted?: number | null;
          tangentsDetectedCount?: number | null;
          tangentsDivertedCount?: number | null;
          decisionCount: number | null;
          actionItemCount?: number | null;
          followUpCount?: number | null;
          outputCount?: number | null;
          interruptionCount: number | null;
          interruptionTimeSavedMinutes?: number | null;
          tangentTimeSavedMinutes?: number | null;
          totalTimeSavedMinutes?: number | null;
          participantStats?: Record<string, { talkTime?: number; interruptions?: number; displayName?: string }> | null;
        }>;
      };
    }>;
  },
  getMeetingStats: (meetingId: string) => apiRequest(`/analytics/meetings/${meetingId}`),
  getParticipantStats: (period = '30d') =>
    apiRequest(`/analytics/participants?period=${period}`),
  getTalkTimeDistribution: (meetingId: string) =>
    apiRequest(`/analytics/meetings/${meetingId}/talk-time`),
  getInterruptionStats: (meetingId: string) =>
    apiRequest(`/analytics/meetings/${meetingId}/interruptions`),
  /** AI focus recommendations for company analysis (cached weekly, 3 bullets). forceRefresh=true triggers on-demand LLM call. */
  getFocusRecommendations: (forceRefresh = false, period?: string) => {
    const params = new URLSearchParams();
    if (forceRefresh) params.set('forceRefresh', 'true');
    if (period) params.set('period', period);
    const qs = params.toString();
    return apiRequest(
      `/analytics/focus-recommendations${qs ? `?${qs}` : ''}`
    ) as Promise<{
      data?: {
        recommendations: Array<{ title: string; detail: string }>;
        generatedAtMs: number;
        fromCache?: boolean;
      };
    }>;
  },
  /** Per-team scorecard aggregations for Company Analysis page (org_admin only). */
  getTeamAnalytics: (period = '30d') =>
    apiRequest(`/analytics/teams?period=${period}`) as Promise<{
      data?: {
        teams: Array<{
          id: string;
          name: string;
          meetingCount: number;
          scores: { talkEquity: number; agendaAdherence: number; interruptions: number; decisionVelocity: number; timeDiscipline: number };
          momentum: number;
          delta: number | null;
          hasData: boolean;
        }>;
        orgMomentum: number | null;
        orgTrend: Array<{
          windowKey: string;
          label: string;
          momentum: number;
          teamCount: number;
          meetingCount: number;
        }>;
        orgCurrentWindow: {
          windowKey: string;
          label: string;
          startMs: number;
          endMs: number;
        } | null;
      };
    }>,
  /**
   * LLM-generated organizational intelligence insights (org_admin only).
   * Cached per org for the latest completed 30-day org-intelligence cycle.
   */
  getOrgIntelligence: (forceRefresh = false) =>
    apiRequest(
      `/analytics/org-intelligence${forceRefresh ? '?forceRefresh=true' : ''}`,
    ) as Promise<{
      data?: {
        insights: Array<{ title: string; detail: string }>;
        generatedAtMs: number;
        fromCache: boolean;
      };
    }>,
  /**
   * Layer 2 learning engine accountability review data (org_admin only).
   * Returns structured data from org_profiles, team_profiles, behavioral_signatures,
   * and layer2_recommendation_history tables.
   */
  getAccountabilityReview: (period = '30d') =>
    apiRequest(`/analytics/accountability-review?period=${encodeURIComponent(period)}`) as Promise<{
      data?: {
        org: {
          worked: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          notWorking: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          next: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
            createdAt?: string;
            rollupCount?: number;
            rollupTeamNames?: string[];
            rollupAppliedCount?: number;
          }>;
          realtime: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          longterm: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          narrative: string | null;
          maturityLevel: string | null;
          confidenceScore: number | null;
          dataSource: 'l2' | 'none';
        };
        teams: Array<{
          teamId: string;
          teamName: string;
          worked: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          notWorking: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          next: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
            createdAt?: string;
            rollupCount?: number;
            rollupTeamNames?: string[];
            rollupAppliedCount?: number;
          }>;
          realtime: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          longterm: Array<{
            id?: string;
            text: string;
            source: string;
            confidence?: number;
            lifecycleStatus?: 'planned' | 'applied';
            configKey?: string;
          }>;
          narrative: string | null;
          maturityLevel: string | null;
          confidenceScore: number | null;
          dataSource: 'l2' | 'none';
        }>;
        recentRecommendations: Array<{
          recommendationId: string;
          teamId: string;
          teamName: string;
          configKey: string;
          rationale: string;
          confidenceScore: number;
          createdAt: string;
          appliedAt: string | null;
          lifecycleStatus: 'planned' | 'applied';
        }>;
        accountabilityMlfBeta?: boolean;
        lastComputedAt: string | null;
        period: string;
      };
    }>,
};

export const settingsAPI = {
  get: () => apiRequest('/settings'),
  update: (settings: object) =>
    apiRequest('/settings', { method: 'PATCH', body: JSON.stringify(settings) }),
  getNotifications: () => apiRequest('/settings/notifications'),
  updateNotifications: (settings: object) =>
    apiRequest('/settings/notifications', { method: 'PATCH', body: JSON.stringify(settings) }),
};

/** Org API: current org/role, create org, list members, invite, remove member, set role. */
export const orgAPI = {
  getMe: () =>
    apiRequest('/org/me') as Promise<{
      data?: { org: { id: string; name: string; slug: string; isPersonal?: boolean } | null; role: string | null };
    }>,
  listOrgs: () =>
    apiRequest('/org/list') as Promise<{
      data?: { orgs: Array<{ id: string; name: string; slug: string; isPersonal: boolean; role: string; isCurrent: boolean }> };
    }>,
  switchOrg: (orgId: string) =>
    apiRequest('/org/switch', { method: 'POST', body: JSON.stringify({ orgId }) }) as Promise<{
      data?: { switched: boolean; orgId: string };
    }>,
  createOrg: (name: string) =>
    apiRequest('/org', { method: 'POST', body: JSON.stringify({ name }) }) as Promise<{ data?: { org: { id: string; name: string; slug: string } } }>,
  getMembers: () =>
    apiRequest('/org/members') as Promise<{ data?: { members: Array<{ id: string; user_unique_uuid: string; email: string; full_name: string | null; name: string; role: string }> } }>,
  removeMember: (userId: string) =>
    apiRequest(`/org/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  setMemberRole: (userId: string, payload: { role: 'admin' | 'member' }) =>
    apiRequest(`/org/members/${encodeURIComponent(userId)}/role`, { method: 'PATCH', body: JSON.stringify(payload) }),
  createInvitation: (payload: { email: string; role?: string }) =>
    apiRequest('/org/invitations', { method: 'POST', body: JSON.stringify(payload) }),
  getInvitations: () =>
    apiRequest('/org/invitations') as Promise<{
      data?: { invitations: Array<{ id: string; email: string; role: string; created_at: string }> };
    }>,
  resendInvitation: (invitationId: string) =>
    apiRequest(`/org/invitations/${encodeURIComponent(invitationId)}/resend`, { method: 'POST' }) as Promise<{
      data?: {
        invitation?: { id: string; email: string; role: string; status: string };
        email?: { sent: boolean; transport: 'postmark' | 'noop'; mode?: string };
        invitee?: {
          hasAccount?: boolean;
          userId?: string;
          orgs?: Array<{ id: string; name: string; slug: string; role: string }>;
        };
      };
    }>,
  revokeInvitation: (invitationId: string) =>
    apiRequest(`/org/invitations/${encodeURIComponent(invitationId)}`, { method: 'DELETE' }) as Promise<{
      data?: { revoked?: boolean; id?: string };
    }>,
  previewInvitation: (token: string) =>
    apiRequest(`/org/invitations/preview?token=${encodeURIComponent(token)}`) as Promise<{
      data?: {
        orgName?: string;
        inviterName?: string;
        role?: string;
        expiresAt?: string | null;
        status?: string;
        inviteStatus?: string;
      };
    }>,
  acceptInvitation: (token: string) =>
    apiRequest('/org/invitations/accept', { method: 'POST', body: JSON.stringify({ token }) }) as Promise<{
      data?: { orgId?: string; orgName?: string; accepted?: boolean };
    }>,
  declineInvitation: (token: string) =>
    apiRequest('/org/invitations/decline', { method: 'POST', body: JSON.stringify({ token }) }) as Promise<{
      data?: { declined?: boolean };
    }>,
};

/** Teams API: list, create, update, archive; add/remove members, set role. */
export const teamsAPI = {
  list: () =>
    apiRequest('/teams') as Promise<{
      teams?: Array<{ id: string; name: string; color?: string; status: string; createdByUserId: string | null; memberCount: number; myRole: string | null }>;
    }>,
  create: (payload: { name: string; color?: string }) =>
    apiRequest('/teams', { method: 'POST', body: JSON.stringify(payload) }) as Promise<{ data?: { team: { id: string; name: string; status: string } } }>,
  get: (teamId: string) =>
    apiRequest(`/teams/${teamId}`) as Promise<{
      data?: { team: { id: string; name: string; color?: string; status: string; createdByUserId: string | null; myRole: string }; members: Array<{ userId: string; email: string; fullName: string | null; role: string }> };
    }>,
  update: (teamId: string, payload: { name?: string; color?: string }) =>
    apiRequest(`/teams/${teamId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  archive: (teamId: string) =>
    apiRequest(`/teams/${teamId}/archive`, { method: 'POST' }),
  addMember: (teamId: string, payload: { userId: string; role?: 'admin' | 'member' }) =>
    apiRequest(`/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify(payload) }),
  removeMember: (teamId: string, userId: string) =>
    apiRequest(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
  setMemberRole: (teamId: string, userId: string, payload: { role: 'admin' | 'member' }) =>
    apiRequest(`/teams/${teamId}/members/${userId}/role`, { method: 'PATCH', body: JSON.stringify(payload) }),
};

/** Badges API: definitions and user/org/team awards (profile, dashboard, team). */
export const badgesAPI = {
  listDefinitions: (category?: 'agenda' | 'performance' | 'outputs' | 'milestone') =>
    apiRequest(`/badges${category ? `?category=${category}` : ''}`) as Promise<{ data?: { badges: Array<{ badgeKey: string; name: string; description: string | null; category: string; iconRef: string | null; displayOrder: number }> } }>,
  getMyBadges: () =>
    apiRequest('/badges/me') as Promise<{ data?: { badges: Array<{ badgeKey: string; awardedAt: string; sourceMeetingId: string | null; count?: number; definition?: { badgeKey: string; name: string; description: string | null; category: string } }> } }>,
  getOrgBadges: (orgId: string) =>
    apiRequest(`/badges/org/${orgId}`) as Promise<{ data?: { badges: Array<{ badgeKey: string; awardedAt: string; sourceMeetingId: string | null; count?: number; definition?: { badgeKey: string; name: string; description: string | null; category: string } }> } }>,
  getTeamBadges: (teamId: string) =>
    apiRequest(`/badges/team/${teamId}`) as Promise<{ data?: { badges: Array<{ badgeKey: string; awardedAt: string; sourceMeetingId: string | null; count?: number; definition?: { badgeKey: string; name: string; description: string | null; category: string } }> } }>,
  getTeamsBadges: () =>
    apiRequest('/badges/teams') as Promise<{
      data?: {
        teams: Array<{
          teamId: string;
          teamName: string;
          badges: Array<{ badgeKey: string; awardedAt: string; sourceMeetingId: string | null; count?: number; definition?: { badgeKey: string; name: string; description: string | null; category: string } }>;
        }>;
      };
    }>,
};

// --------------------------------------------------------------------------
// Platform Admin API (owner-only: tim@rithmo.ai, raghu@rithmo.ai)
// --------------------------------------------------------------------------

/** The five lanes shared by the customer + platform Learning Map responses. */
export interface LearningMapLanes {
  marchingOrders: {
    activeCount: number;
    byGoalArea: Array<{ goalArea: string; goalAreaLabel: string; count: number }>;
    lastActivityMs: number | null;
  };
  guardrails: {
    allowed: number;
    blocked: number;
    deferred: number;
    total: number;
    lastActivityMs: number | null;
  };
  reflexes: {
    totalChanges: number;
    teamsTuned: number;
    knobs: Array<{
      key: string;
      label: string;
      teamsSet: number;
      distribution: Array<{ value: string; teams: number }>;
    }>;
    lastActivityMs: number | null;
  };
  memory: {
    peopleProfiled: number;
    teamsUnderstood: number;
    orgsMapped: number;
    behavioralSignals: number;
    strategicInsights: number;
    lastActivityMs: number | null;
  };
  judgment: {
    liveDecisions: number;
    interventions: number;
    lastActivityMs: number | null;
  };
}

export interface LearningMapResponse {
  scope: 'customer' | 'platform';
  lanes: LearningMapLanes;
}

/** Phase 2 — per-layer force-directed graph for one customer. */
export interface LearningGraphNode {
  id: string;
  type: string;
  label: string;
  group: string;
  value?: number;
}
export interface LearningGraphEdge {
  source: string;
  target: string;
  kind?: string;
  weight?: number;
}
export interface LearningGraphResponse {
  scope: 'customer';
  orgId: string;
  layer: string;
  nodes: LearningGraphNode[];
  edges: LearningGraphEdge[];
}

/** Phase 3 — history/trace timeline for one node in a layer graph. */
export interface LearningNodeEvent {
  ts: number;
  label: string;
  detail?: string;
  status: string;
}
export interface LearningNodeResponse {
  nodeId: string;
  type: string;
  title: string;
  subtitle: string;
  attributes?: Array<{ label: string; value: string }>;
  narrative?: string;
  events: LearningNodeEvent[];
}

export interface StrategicMetricsResponse {
  scope: 'customer';
  org: { orgId: string; orgName: string };
  strategic: {
    suggestions: {
      recommended: number;
      placedByRithmo: number;
      placedByHuman: number;
      suggested: number;
      accepted: number;
      rejected: number;
      acceptanceRate: number;
      lastActivityMs: number | null;
    };
    timeOptimization: {
      meetingsMeasured: number;
      avgScheduledMin: number;
      avgActualMin: number;
      avgOverrunMin: number;
      avgLingerMin: number;
      onTimeRate: number;
    };
    agendaTopics: Array<{
      title: string;
      samples: number;
      plannedMin: number;
      actualMin: number;
      deltaMin: number;
    }>;
    proxy: {
      meetingsRun: number;
      goalsTotal: number;
      goalsSatisfied: number;
      goalsBlocked: number;
      goalsOpen: number;
      achievementRate: number;
    };
    chat: {
      setupThreads: number;
      setupMessages: number;
      prefsExtracted: number;
    };
    notTracked: { proxyTraining: boolean; liveMeetingChat: boolean };
  };
}

export const platformAdminAPI = {
  getOverview: (period = '30d') =>
    apiRequest(`/admin/overview?period=${period}`) as Promise<{
      data?: {
        period: string;
        periodDays: number;
        totals: {
          users: number;
          verifiedUsers: number;
          zoomConnected: number;
          orgs: number;
          meetingsEnded: number;
          meetingsAll: number;
          activeMeetings: number;
        };
        inPeriod: {
          newUsers: number;
          newOrgs: number;
          meetingsEnded: number;
          meetingsStarted: number;
          completionRate: number | null;
          recurringMeetings: number;
          botSessions: number;
          avgBotSessionMs: number | null;
          totalBotSessionMs: number | null;
          botEstimatedCostUsd: number | null;
          llmInputTokens: number;
          llmOutputTokens: number;
          llmCalls: number;
          ttsChars: number;
          ttsCalls: number;
          ttsAudioMs: number;
          emailSends: number;
        };
      };
    }>,

  listMeetingIntelligence: () =>
    apiRequest('/admin/meeting-intelligence/list') as Promise<{
      data?: {
        meetings: Array<{
          meeting_id: string;
          title: string | null;
          status: string;
          learning_status: string | null;
          is_recurring: boolean;
          parent_meeting_id: string | null;
          recurring_series_id: string | null;
          ended_at: string | null;
          model_count: number | string;
        }>;
      };
    }>,

  getMeetingIntelligence: (meetingId: string) =>
    apiRequest(`/admin/meeting-intelligence/${encodeURIComponent(meetingId)}`) as Promise<{
      data?: Record<string, unknown>;
    }>,

  getLearningOverview: (params?: { since?: string; until?: string }) => {
    const qs = params
      ? '?' + Object.entries(params)
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return apiRequest(`/admin/learning-overview${qs}`) as Promise<{
      data?: {
        filters: { since: number | null; until: number | null };
        summary: {
          learningCompletedMeetings: number;
          learningPendingMeetings: number;
          activeDirectives: number;
          acceptedDirectives: number;
          activeLearnedConfigRows: number;
          teamLevelLearnedConfigRows: number;
          meetingTypeLearnedConfigRows: number;
          appliedChangesCount: number;
          blockedChangesCount: number;
          notTraceableDirectivesCount: number;
          lastLearningActivityAt: string | null;
        };
        healthSignals: {
          learningActivityStatus: 'active' | 'quiet' | 'no_data';
          directiveTraceabilityStatus: 'healthy' | 'partial' | 'poor' | 'no_data';
          blockedChangeStatus: 'normal' | 'elevated' | 'high' | 'no_data';
          dataFreshnessStatus: 'fresh' | 'stale' | 'unknown';
        };
        topCustomers: Array<{
          orgId: string;
          orgName: string;
          activeDirectives: number;
          learnedConfigCount: number;
          appliedChangesCount: number;
          blockedChangesCount: number;
          recentMeetingCount: number;
          lastActivityAt: string | null;
        }>;
        topTeams: Array<{
          teamId: string;
          teamName: string;
          orgId: string | null;
          orgName: string | null;
          learnedConfigCount: number;
          activeDirectives: number;
          appliedChangesCount: number;
          blockedChangesCount: number;
          lastActivityAt: string | null;
        }>;
        recentActivity: Array<{
          eventType: string;
          occurredAtMs: number;
          occurredAt: string;
          businessSummary: string;
          orgId: string | null;
          orgName: string | null;
          teamId: string | null;
          teamName: string | null;
          source: string;
          traceability: string;
        }>;
        emptyStates: {
          noLearningData: boolean;
          noDirectives: boolean;
          noLearnedConfig: boolean;
          message: string | null;
        };
      };
    }>;
  },

  getUsers: (params: { page?: number; limit?: number; search?: string } = {}) =>
    apiRequest(`/admin/users?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))
    )}`) as Promise<{ data?: unknown[]; meta?: { pagination?: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } } }>,

  getMeetings: (params: { page?: number; limit?: number; status?: string; from?: string; to?: string; search?: string } = {}) =>
    apiRequest(`/admin/meetings?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))
    )}`) as Promise<{ data?: unknown[]; meta?: { pagination?: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } } }>,

  getMeetingsStats: (params: { status?: string; from?: string; to?: string } = {}) =>
    apiRequest(`/admin/meetings/stats?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))
    )}`) as Promise<{ data?: { totalMeetings: number; totalMinutes: number } }>,

  getOrganizations: (params: { page?: number; limit?: number; search?: string } = {}) =>
    apiRequest(`/admin/organizations?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))
    )}`) as Promise<{ data?: unknown[]; meta?: { pagination?: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } } }>,

  getTeams: (params: { page?: number; limit?: number; search?: string } = {}) =>
    apiRequest(`/admin/teams?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))
    )}`) as Promise<{ data?: unknown[]; meta?: { pagination?: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } } }>,

  getUsage: (period = '30d') =>
    apiRequest(`/admin/usage?period=${period}`) as Promise<{
      data?: {
        period: string;
        periodDays: number;
        llm: {
          byTaskType: Array<{ taskType: string; model: string; provider: string; inputTokens: number; outputTokens: number; calls: number; avgLatencyMs: number | null }>;
          dailyTrend: Array<{ day: string; inputTokens: number; outputTokens: number; calls: number }>;
        };
        tts: { byProvider: Array<{ provider: string; model: string; totalChars: number; calls: number; totalAudioMs: number }> };
        email: { byType: Array<{ emailType: string; sends: number }> };
        recallBot?: { sessions: number; totalMinutes: number; avgMinutes: number | null; estimatedCostUsd: number | null; costPerMinute: number };
      };
    }>,

  getTeamLearnedConfig: (teamId: string) =>
    apiRequest(`/admin/teams/${encodeURIComponent(teamId)}/learned-config`) as Promise<{
      data?: {
        teamId: string;
        teamName: string | null;
        orgId: string | null;
        orgName: string | null;
        teamFound: boolean;
        currentTeamConfig: Array<{
          configKey: string;
          humanLabel: string;
          currentValue: unknown;
          previousValue: unknown;
          source: string;
          proposalSource: string;
          proposalSourceRaw: string | null;
          directiveId: string | null;
          proposalId: string | null;
          decisionId: string | null;
          appliedAt: string | null;
          provenanceAvailable: boolean;
        }>;
        meetingTypeConfig: Array<{
          meetingTypeId: string;
          configKey: string;
          humanLabel: string;
          currentValue: unknown;
          previousValue: unknown;
          source: string;
          proposalSource: string;
          proposalSourceRaw: string | null;
          directiveId: string | null;
          proposalId: string | null;
          decisionId: string | null;
          appliedAt: string | null;
          provenanceAvailable: boolean;
        }>;
        summary: {
          teamConfigCount: number;
          meetingTypeConfigCount: number;
          directiveDrivenCount: number;
          organicCount: number;
          notTraceableCount: number;
        };
      };
    }>,

  getTeamLearnedConfigHistory: (
    teamId: string,
    params: { configKey?: string; since?: number; until?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.configKey) qs.set('configKey', params.configKey);
    if (params.since != null) qs.set('since', String(params.since));
    if (params.until != null) qs.set('until', String(params.until));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest(`/admin/teams/${encodeURIComponent(teamId)}/learned-config/history${query}`) as Promise<{
      data?: {
        teamId: string;
        configKeyFilter: string | null;
        sinceFilter: string | null;
        untilFilter: string | null;
        history: Array<{
          configKey: string;
          humanLabel: string;
          action: string;
          oldValue: unknown;
          newValue: unknown;
          changedAt: string | null;
          proposalSource: string;
          proposalSourceRaw: string | null;
          directiveId: string | null;
          proposalId: string | null;
          decisionId: string | null;
          reason: string | null;
          meetingTypeId: string | null;
          scope: 'team' | 'meeting_type';
          provenanceAvailable: boolean;
        }>;
        totalRows: number;
      };
    }>;
  },

  getMeetingImpact: (meetingId: string) =>
    apiRequest(`/admin/meetings/${encodeURIComponent(meetingId)}/impact`) as Promise<{
      data?: {
        meetingId: string;
        orgId: string | null;
        teamIds: string[];
        recurringSeriesId: string | null;
        isRecurring: boolean;
        meetingEndedAt: string | null;
        linkage: {
          method: 'approximate_time_window' | 'not_traceable';
          note: string;
        };
        guardrailDecisions: Array<{
          decisionId: string;
          decidedAt: string | null;
          teamId: string;
          decision: string;
          reasons: Array<unknown>;
          proposalSource: string;
          directiveId: string | null;
          applyStatus: string | null;
          changesSummary: Array<unknown>;
          confidenceHint: unknown | null;
        }>;
        appliedChanges: Array<{
          team_id: string;
          config_key: string;
          config_value: unknown;
          source: string;
          proposal_id: string;
          decision_id: string;
          applied_at: string;
          previous_value: unknown;
          configScope: 'team' | 'meeting_type';
          meeting_type_id?: string;
        }>;
        summary: {
          totalDecisions: number;
          allowed: number;
          denied: number;
          deferred: number;
          appliedConfigChanges: number;
          directiveDriven: number;
          organic: number;
        };
      };
    }>,

  getUserLearningProfile: (userId: string) =>
    apiRequest(`/admin/users/${encodeURIComponent(userId)}/learning-profile`) as Promise<{
      data?: {
        userId: string;
        user: {
          userId: string;
          email: string;
          displayName: string | null;
          timezone: string | null;
          zoomConnected: boolean;
          zoomEmail: string | null;
          primaryOrgId: string | null;
        };
        orgs: Array<{ orgId: string; orgName: string | null; role: string }>;
        teams: Array<{ teamId: string; teamName: string | null; orgId: string | null; orgName: string | null }>;
        learningProfile: {
          profileFound: boolean;
          profileSource: string;
          meetingCount: number | null;
          mostRecentMeetingAt: string | null;
          firstSeenAt: string | null;
          participationTrend: string | null;
          businessSummary: string | null;
          computedAt: string | null;
          windowLabel: string | null;
          contextMemoriesTotal: number;
          contextMemories: Array<{
            memoryId: string;
            memoryType: string;
            summaryText: string;
            confidenceScore: number;
            sourceMeetingId: string;
            approvedForProductSurface: boolean;
            lastSeenAt: string;
          }>;
        };
        setupDirectives: Array<{
          directiveId: string;
          status: string;
          statusLabel: string;
          scope: string;
          goalArea: string;
          goalAreaLabel: string;
          requestedIntensity: string | null;
          approvedStartingPosture: string;
          approvedStartingPostureLabel: string;
          approvedMaxPosture: string;
          approvedMaxPostureLabel: string;
          escalationAllowed: boolean;
          measurementWindowDays: number;
          executiveMessage: string;
          affectedAreas: string[];
          setupAnchorMeetingId: string | null;
          createdAtMs: number;
          acceptedAtMs: number | null;
          supersededAtMs: number | null;
          supersededByDirectiveId: string | null;
          latestEventType: string | null;
          latestEventAtMs: number | null;
        }>;
        directiveInfluence: {
          windowDays: number;
          totalDecisions: number;
          appliedCount: number;
          blockedCount: number;
          organicAppliedCount: number;
          topAffectedAreas: Array<{ configKey: string; humanLabel: string; count: number }>;
          directiveInfluenceSharePct: number;
          lastAppliedAt: number | null;
          notTraceable: boolean;
        } | null;
        meetings: Array<{
          meetingId: string;
          title: string | null;
          status: string;
          learningStatus: string;
          isRecurring: boolean;
          endedAtMs: number | null;
          orgId: string | null;
          role: string;
          hasParticipantAnalytics: boolean;
        }>;
        contributionSummary: {
          meetingsInWindow: number;
          oneOffMeetings: number;
          recurringMeetings: number;
          teamsConnected: number;
          orgsConnected: number;
          totalDirectives: number;
          activeDirectives: number;
          appliedChangesTraceableToDirectives: number | null;
          blockedChangesTraceableToDirectives: number | null;
          memoriesTotal: number;
          profileFound: boolean;
        };
        debug: {
          aggregationKey: string;
          activeDirectiveIds: string[];
          teamsRaw: string[];
          orgsRaw: string[];
        };
      };
    }>,

  listDirectives: (params: {
    page?: number;
    limit?: number;
    orgId?: string;
    userId?: string;
    teamId?: string;
    status?: string;
    scope?: string;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.page != null) qs.set('page', String(params.page));
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.orgId) qs.set('orgId', params.orgId);
    if (params.userId) qs.set('userId', params.userId);
    if (params.teamId) qs.set('teamId', params.teamId);
    if (params.status) qs.set('status', params.status);
    if (params.scope) qs.set('scope', params.scope);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest(`/admin/directives${query}`) as Promise<{
      data?: {
        summary: {
          total: number;
          active: number;
          paused: number;
          superseded: number;
          reverted: number;
        };
        directives: Array<{
          directiveId: string;
          subjectUserId: string;
          subjectEmail: string | null;
          subjectName: string | null;
          orgId: string | null;
          orgName: string | null;
          teamId: string | null;
          teamName: string | null;
          scope: string;
          scopeLabel: string;
          status: string;
          statusLabel: string;
          goalArea: string;
          goalAreaLabel: string;
          requestedIntensity: string | null;
          urgency: string | null;
          executiveMessage: string;
          createdAtMs: number;
          acceptedAtMs: number | null;
          impactSummary: {
            guardrailDecisionCount: number;
            appliedCount: number;
            blockedCount: number;
            hasTraceableImpact: boolean;
          };
          plainEnglishSummary: string;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      };
    }>;
  },

  getDirectiveImpact: (directiveId: string) =>
    apiRequest(`/admin/directives/${encodeURIComponent(directiveId)}/impact`) as Promise<{
      data?: {
        directive: {
          directiveId: string;
          subjectUserId: string;
          subjectEmail: string | null;
          subjectName: string | null;
          authorUserId: string;
          orgId: string | null;
          orgName: string | null;
          teamId: string | null;
          teamName: string | null;
          scope: string;
          scopeLabel: string;
          status: string;
          statusLabel: string;
          goalArea: string;
          goalAreaLabel: string;
          requestedIntensity: string | null;
          urgency: string | null;
          approvedStartingPosture: string;
          approvedMaxPosture: string;
          executiveMessage: string;
          createdAtMs: number;
          acceptedAtMs: number | null;
          plainEnglishSummary: string;
        };
        lifecycleEvents: Array<{
          eventId: string;
          eventType: string;
          eventTypeLabel: string;
          actorUserId: string;
          reason: string | null;
          occurredAtMs: number;
        }>;
        impactSummary: {
          guardrailDecisionCount: number;
          appliedCount: number;
          blockedCount: number;
          appliedLearningCount: number;
          traceability: {
            method: 'directive_id_match' | 'not_traceable';
            note: string;
          };
        };
        guardrailDecisions: Array<{
          recordId: string;
          decisionId: string;
          decision: string;
          applyStatus: string;
          configKey: string | null;
          proposedValue: unknown;
          teamId: string;
          recordedAtMs: number;
        }>;
        appliedLearning: Array<{
          configKey: string;
          configValue: unknown;
          scope: 'team' | 'meeting_type';
          meetingTypeId: string | null;
          teamId: string;
          decisionId: string;
          appliedAt: string | null;
        }>;
      };
    }>,

  getSeriesProfile: (seriesId: string) =>
    apiRequest(`/admin/series/${encodeURIComponent(seriesId)}/profile`) as Promise<{
      data?: {
        seriesId: string;
        series: {
          seriesKey: string;
          orgId: string | null;
          maturityLevel: string | null;
          coldStartStage: string | null;
          confidence: number | null;
          identitySource: string | null;
          createdAt: string | null;
          foundBy: string;
        };
        learningProfile: {
          profileFound: boolean;
          aiNarrative: string | null;
          instanceCount: number;
          firstInstanceAt: string | null;
          lastInstanceAt: string | null;
          confidence: number | null;
          maturityLevel: string | null;
          coldStartStage: string | null;
          computedAt: string | null;
        };
        occurrenceHistory: Array<{
          meetingId: string;
          title: string | null;
          status: string | null;
          learningStatus: string;
          meetingTypeId: string | null;
          endedAtMs: number | null;
          source: 'layer2' | 'legacy';
          linkConfidence: number | null;
        }>;
        learnedConfig: Array<{
          teamId: string;
          teamName: string | null;
          meetingTypeId: string;
          configKey: string;
          configLabel: string;
          configValue: unknown;
          appliedAt: string | null;
        }>;
        configHistory: Array<{
          teamId: string;
          meetingTypeId: string;
          configKey: string;
          configLabel: string;
          action: string;
          oldValue: unknown;
          newValue: unknown;
          occurredAt: string;
        }>;
        directives: Array<{
          directiveId: string;
          status: string;
          primaryGoalArea: string;
          goalAreaLabel: string;
          scope: string;
          createdAtMs: number;
        }>;
        trendSummary: {
          maturityLevel: string | null;
          coldStartStage: string | null;
          confidence: number | null;
          instanceCount: number;
          growthDirection: string;
          firstInstanceAt: string | null;
          lastInstanceAt: string | null;
          note: string;
        };
        debug: {
          seriesId: string;
          foundBy: string;
          orgId: string | null;
          meetingIds: string[];
          meetingTypeIds: string[];
          teamIds: string[];
        };
      };
    }>,

  getLearningTimeline: (params?: {
    orgId?: string;
    teamId?: string;
    userId?: string;
    since?: string;
    until?: string;
    eventType?: string;
    page?: number;
    limit?: number;
  }) => {
    const qs = params
      ? '?' + Object.entries(params)
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return apiRequest(`/admin/learning-timeline${qs}`) as Promise<{
      data?: {
        filters: {
          orgId: string | null;
          teamId: string | null;
          userId: string | null;
          since: number | null;
          until: number | null;
          eventType: string | null;
        };
        summary: {
          totalEvents: number;
          directiveEventCount: number;
          learningChangeCount: number;
          guardrailAllowedCount: number;
          guardrailBlockedCount: number;
          note: string | null;
        };
        events: Array<{
          eventId: string;
          eventType: string;
          eventTypeLabel: string;
          occurredAtMs: number;
          teamId: string | null;
          teamName: string | null;
          orgId: string | null;
          orgName: string | null;
          actorUserId: string | null;
          actorUserName: string | null;
          directiveId: string | null;
          directiveGoalArea: string | null;
          directiveGoalAreaLabel: string | null;
          configKey: string | null;
          configLabel: string | null;
          newValue: unknown;
          oldValue: unknown;
          summary: string;
          source: string;
        }>;
        groupedByDay: Array<{
          date: string;
          count: number;
          events: Array<{
            eventId: string;
            eventType: string;
            eventTypeLabel: string;
            occurredAtMs: number;
            summary: string;
          }>;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
        debug: {
          teamIdsForOrg: string[];
          directiveIdsForUser: string[];
          sourceCounts: {
            directiveEvents: number;
            teamConfigAudit: number;
            mtConfigAudit: number;
            guardrailDecisions: number;
          };
        };
      };
    }>;
  },

  getOrgLearningSummary: (orgId: string) =>
    apiRequest(`/admin/orgs/${encodeURIComponent(orgId)}/learning-summary`) as Promise<{
      data?: {
        orgId: string;
        org: {
          orgId: string;
          orgName: string;
          orgSlug: string;
          createdAt: string;
          userCount: number;
          teamCount: number;
          meetingCount: number;
        };
        learningSummary: {
          orgProfileFound: boolean;
          orgAiNarrative: string | null;
          windowLabel: string | null;
          computedAt: string | null;
          compositionMaturityLevel: string | null;
          profileConfidenceScore: number | null;
          systemicIssues: string[];
          systemicThemes: string[];
          policyEffectivenessAvailable: boolean;
          insufficientEvidence: boolean;
          sparseSignal: boolean;
        };
        teams: Array<{
          teamId: string;
          teamName: string | null;
          memberCount: number;
          meetingCount: number;
          learnedConfigCount: number;
          teamLevelConfigCount: number;
          meetingTypeConfigCount: number;
          activeDirectiveCount: number;
          lastLearningActivityAt: string | null;
        }>;
        directives: {
          totalDirectives: number;
          activeDirectives: number;
          pausedDirectives: number;
          supersededDirectives: number;
          revertedDirectives: number;
          directivesWithTraceableImpact: number;
          directivesWithoutTraceableImpact: number;
          topGoalAreas: Array<{ goalArea: string; goalAreaLabel: string; count: number }>;
        };
        appliedLearning: {
          totalActiveLearnedConfigRows: number;
          teamLevelConfigCount: number;
          meetingTypeConfigCount: number;
          lastAppliedAt: string | null;
          topChangedConfigAreas: Array<{ configKey: string; humanLabel: string; count: number }>;
          traceability: string;
        };
        meetings: {
          totalMeetings: number;
          learningCompletedCount: number;
          oneOffMeetingCount: number;
          recurringMeetingCount: number;
          recentMeetings: Array<{
            meetingId: string;
            title: string | null;
            status: string;
            learningStatus: string;
            isRecurring: boolean;
            endedAtMs: number | null;
            recurringSeriesId: string | null;
          }>;
        };
        people: {
          totalUsers: number;
          usersWithProfiles: number;
          usersWithActiveDirectives: number;
        };
        traceability: {
          orgProfile: string;
          teams: string;
          directives: string;
          appliedLearning: string;
          meetings: string;
          note: string;
        };
        debug: {
          orgId: string;
          teamIds: string[];
          directiveIds: string[];
          orgProfileFound: boolean;
          totalTeamConfigRows: number;
          totalMtConfigRows: number;
        };
      };
    }>,

  listProposals: (params?: {
    orgId?: string;
    teamId?: string;
    meetingId?: string;
    directiveId?: string;
    source?: string;
    decision?: string;
    since?: string;
    until?: string;
    page?: number;
    limit?: number;
  }) => {
    const qs = params
      ? '?' + Object.entries(params)
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return apiRequest(`/admin/proposals${qs}`) as Promise<{
      data?: {
        proposals: Array<{
          proposalId: string;
          teamId: string;
          teamName: string | null;
          orgId: string | null;
          orgName: string | null;
          meetingId: string | null;
          directiveId: string | null;
          proposalSource: string;
          proposalSourceLabel: string;
          changes: Array<{ key: string; from_value: unknown; to_value: unknown; change_type: string }>;
          changeSummary: string;
          evidence: { window_start: number; window_end: number; sample_size: number; metrics: Record<string, number> } | null;
          reason: string | null;
          proposedAt: string | null;
          guardrailDecision: string | null;
          guardrailDecisionLabel: string;
          applyStatus: string | null;
          applyStatusLabel: string | null;
        }>;
        pagination: { page: number; limit: number; total: number; totalPages: number };
      };
    }>;
  },

  getProposalDetail: (proposalId: string) =>
    apiRequest(`/admin/proposals/${encodeURIComponent(proposalId)}`) as Promise<{
      data?: {
        proposal: {
          proposalId: string;
          teamId: string;
          teamName: string | null;
          orgId: string | null;
          orgName: string | null;
          meetingId: string | null;
          meetingTitle: string | null;
          directiveId: string | null;
          directiveMessage: string | null;
          primaryGoalArea: string | null;
          proposalSource: string;
          proposalSourceLabel: string;
          changes: Array<{ key: string; from_value: unknown; to_value: unknown; change_type: string }>;
          beforeAfter: Array<{
            configKey: string;
            changeType: string;
            changeTypeLabel: string;
            beforeValue: unknown;
            afterValue: unknown;
            businessSummary: string;
          }>;
          evidence: { window_start: number; window_end: number; sample_size: number; metrics: Record<string, number> } | null;
          reason: string | null;
          confidenceHint: unknown | null;
          proposedAt: string | null;
          guardrailDecision: {
            decision: string;
            decisionLabel: string;
            applyStatus: string | null;
            applyStatusLabel: string | null;
            reasons: Array<{ code: string; message: string }>;
            decidedAt: string | null;
            evidenceSampleSize: number | null;
          } | null;
        };
      };
    }>,

  getCustomerLearning360: (orgId: string) =>
    apiRequest(`/admin/customers/${encodeURIComponent(orgId)}/learning-360`) as Promise<{
      data?: {
        orgId: string;
        org: {
          orgId: string;
          orgName: string;
          orgSlug: string;
          createdAt: string;
          userCount: number;
          teamCount: number;
          meetingCount: number;
        };
        summary: {
          totalLearnedConfigRows: number;
          activeDirectiveCount: number;
          recentProposalCount: number;
          profileCoverage: number;
        };
        topTeams: Array<{
          teamId: string;
          teamName: string | null;
          memberCount: number;
          learnedConfigCount: number;
          activeDirectiveCount: number;
          lastActivityAt: string | null;
        }>;
        activeDirectives: Array<{
          directiveId: string;
          subjectName: string | null;
          subjectEmail: string | null;
          teamName: string | null;
          scope: string;
          scopeLabel: string;
          status: string;
          statusLabel: string;
          goalArea: string;
          goalAreaLabel: string;
          executiveMessage: string;
          acceptedAtMs: number | null;
        }>;
        recentProposals: Array<{
          proposalId: string;
          teamId: string;
          teamName: string | null;
          proposalSource: string;
          proposalSourceLabel: string;
          changeSummary: string;
          changeCount: number;
          reason: string | null;
          proposedAtMs: number;
          decision: string | null;
          decisionLabel: string;
          applyStatus: string | null;
          applyStatusLabel: string | null;
        }>;
        recentGuardrailActivity: Array<{
          decision: string;
          decisionLabel: string;
          applyStatus: string | null;
          applyStatusLabel: string | null;
          teamId: string;
          configKey: string | null;
          humanLabel: string | null;
          proposalSource: string | null;
          proposalSourceLabel: string | null;
          directiveId: string | null;
          decidedAtMs: number;
        }>;
        recentLearningMeetings: Array<{
          meetingId: string;
          title: string | null;
          learningStatus: string;
          isRecurring: boolean;
          endedAtMs: number | null;
        }>;
        peopleCoverage: {
          totalUsers: number;
          usersWithProfiles: number;
          usersWithActiveDirectives: number;
          profileCoverage: number;
        };
        recentTimeline: Array<{
          source: 'directive' | 'learning';
          eventType: string;
          eventLabel: string;
          context: string;
          subjectName: string | null;
          occurredAtMs: number;
        }>;
      };
    }>,

  // Phase 8a — admin-editable stoplist CRUD (layers on top of hard-coded
  // src/shared/learningEngineStoplist.ts patterns).
  listStoplistEntries: (params?: { scope?: string; includeDisabled?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.scope) qs.set('scope', params.scope)
    if (params?.includeDisabled) qs.set('includeDisabled', 'true')
    const tail = qs.toString() ? `?${qs.toString()}` : ''
    return apiRequest(`/admin/stoplist/entries${tail}`) as Promise<{
      data?: { entries: Array<{
        id: string; pattern_kind: string; pattern_value: string;
        scope_org_id: string | null; reason: string; created_by: number | null;
        created_at: string; updated_at: string; disabled_at: string | null;
      }> };
    }>
  },
  createStoplistEntry: (body: {
    patternKind: 'email' | 'display_name' | 'email_domain';
    patternValue: string;
    scopeOrgId?: string | null;
    reason?: string;
  }) => apiRequest('/admin/stoplist/entries', { method: 'POST', body: JSON.stringify(body) }) as Promise<{ data?: { entry: unknown } }>,
  updateStoplistEntry: (id: string, body: { reason?: string; patternValue?: string; disable?: boolean; enable?: boolean }) =>
    apiRequest(`/admin/stoplist/entries/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }) as Promise<{ data?: { entry: unknown } }>,
  deleteStoplistEntry: (id: string) =>
    apiRequest(`/admin/stoplist/entries/${encodeURIComponent(id)}`, { method: 'DELETE' }) as Promise<{ data?: { deletedId: string } }>,

  // Learning Map — 5-lane radial summary across the 3 learning layers
  getCustomerLearningMap: (orgId: string) =>
    apiRequest(`/admin/customers/${encodeURIComponent(orgId)}/learning-map`) as Promise<{
      data?: LearningMapResponse & {
        scope: 'customer';
        org: {
          orgId: string;
          orgName: string;
          orgSlug: string;
          createdAt: string;
          userCount: number;
          teamCount: number;
          meetingCount: number;
        };
      };
    }>,

  getPlatformLearningMap: () =>
    apiRequest('/admin/learning-map') as Promise<{
      data?: LearningMapResponse & {
        scope: 'platform';
        platform: {
          orgCount: number;
          userCount: number;
          teamCount: number;
          meetingCount: number;
        };
        customers: Array<{
          orgId: string;
          orgName: string;
          orgSlug: string;
          userCount: number;
          learnedChanges: number;
          activeDirectives: number;
          lastActivityMs: number | null;
        }>;
      };
    }>,

  getCustomerLearningGraph: (orgId: string, layer: string, lens?: string) =>
    apiRequest(`/admin/customers/${encodeURIComponent(orgId)}/learning-graph?layer=${encodeURIComponent(layer)}${lens ? `&lens=${encodeURIComponent(lens)}` : ''}`) as Promise<{
      data?: LearningGraphResponse;
    }>,

  getCustomerLearningNode: (orgId: string, nodeId: string, layer: string) =>
    apiRequest(`/admin/customers/${encodeURIComponent(orgId)}/learning-node?nodeId=${encodeURIComponent(nodeId)}&layer=${encodeURIComponent(layer)}`) as Promise<{
      data?: LearningNodeResponse;
    }>,

  getCustomerStrategicMetrics: (orgId: string) =>
    apiRequest(`/admin/customers/${encodeURIComponent(orgId)}/strategic-metrics`) as Promise<{
      data?: StrategicMetricsResponse;
    }>,

  // LLM Intelligence Pipeline — 3-step pipeline viewer
  listLlmPipelineMeetings: () =>
    apiRequest('/admin/llm-pipeline/list') as Promise<{
      data?: {
        meetings: Array<{
          meeting_id: string;
          title: string | null;
          org_id: string | null;
          status: string;
          learning_status: string | null;
          is_recurring: boolean;
          ended_at: string | null;
          has_step1: boolean;
          step1_computed_at: string | null;
          step1_model: string | null;
          step1_latency_ms: number | null;
          step1_input_tokens: number | null;
          step1_output_tokens: number | null;
          has_step2: boolean;
          step2_computed_at: string | null;
          step2_model: string | null;
          step2_latency_ms: number | null;
          step2_input_tokens: number | null;
          step2_output_tokens: number | null;
          step3_changelog_count: number | string;
        }>;
      };
    }>,

  getLlmPipelineDetail: (meetingId: string) =>
    apiRequest(`/admin/llm-pipeline/${encodeURIComponent(meetingId)}`) as Promise<{
      data?: {
        meeting: Record<string, unknown> | null;
        pipelineStatus: { step1: string; step2: string; step3: string };
        timing: {
          step1ComputedAt: string | null;
          step1LatencyMs: number | null;
          step2ComputedAt: string | null;
          step2LatencyMs: number | null;
          step3FirstChangeAt: string | null;
          totalTokensIn: number;
          totalTokensOut: number;
        };
        step1: Record<string, unknown> | null;
        step2: Record<string, unknown> | null;
        step3: {
          changelog: Array<Record<string, unknown>>;
          changelogCount: number;
          currentSummaries: Record<string, unknown>;
        };
        layer2?: Record<string, unknown>;
      };
    }>,
};

export const supportAPI = {
  submitTicket: (data: { name: string; email: string; subject?: string; message: string }) =>
    apiRequest('/support/ticket', { method: 'POST', body: JSON.stringify(data) }) as Promise<{
      ticketId: string;
      message: string;
      createdAt: string;
    }>,

  /** Whitepaper gate form at /whitepaper-contact — stored in support_tickets with a fixed subject */
  submitWhitepaperRequest: (data: { name: string; email: string; company: string }) =>
    supportAPI.submitTicket({
      name: data.name,
      email: data.email,
      subject: WHITEPAPER_DOWNLOAD_SUBJECT,
      message: formatWhitepaperRequestMessage(data.company),
    }),

  /** General contact form at /contact — stored in support_tickets */
  submitContactInquiry: (data: {
    name: string;
    email: string;
    company: string;
    inquiryType: string;
    message?: string;
  }) =>
    supportAPI.submitTicket({
      name: data.name.trim(),
      email: data.email.trim(),
      subject: contactUsSubject(data.inquiryType),
      message: formatContactUsMessage(data.company, data.inquiryType, data.message),
    }),
};

/** Stripe billing API: plans, subscription, checkout, upgrade/downgrade, invoices, billing status. */
export const stripeAPI = {
  getPlans: () =>
    apiRequest('/stripe/plans') as Promise<{ data?: { plans: Array<{ plan_key: string; name: string; monthly_price_cents: number; included_minutes: number; overage_price_cents_per_hour: number; features: string[]; display_order: number }> } }>,
  getSubscription: () =>
    apiRequest('/stripe/subscription') as Promise<{
      data?: { plan: string; status: string; currentPeriodEnd?: string; cancelAtPeriodEnd?: boolean };
    }>,
  getBillingStatus: () =>
    apiRequest('/stripe/billing-status') as Promise<{
      data?: {
        enforced: boolean;
        plan: { planKey: string; name: string; includedMinutes: number } | null;
        usage: { usedMinutes: number; includedMinutes: number; remainingMinutes: number } | null;
        canStartMeeting: boolean;
      };
    }>,
  getInvoices: () =>
    apiRequest('/stripe/invoices') as Promise<{
      data?: { invoices: Array<{ id: string; number: string; status: string; amountPaid: number; currency: string; created: string; hostedInvoiceUrl: string | null }> };
    }>,
  createCheckoutSession: (plan: string) =>
    apiRequest('/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }) as Promise<{ data?: { url: string } }>,
  upgrade: (plan: string) =>
    apiRequest('/stripe/upgrade', { method: 'POST', body: JSON.stringify({ plan }) }),
  downgrade: (plan: string) =>
    apiRequest('/stripe/downgrade', { method: 'POST', body: JSON.stringify({ plan }) }),
};

// ---------------------------------------------------------------------------
// Brief API — Leader Proxy Brief CRUD
// ---------------------------------------------------------------------------

export const briefAPI = {
  get: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/brief`) as Promise<{ brief: unknown }>,
  save: (meetingId: string, brief: unknown) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/brief`, {
      method: 'PUT',
      body: JSON.stringify(brief),
    }) as Promise<{ ok: true }>,
  remove: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/brief`, { method: 'DELETE' }),
  turn: (
    meetingId: string,
    content: string,
    sourceType: 'voice_transcript' | 'chat_text',
    priorMessages?: Array<{ role: 'user' | 'assistant'; content: string }>,
    userTimezone?: string,
  ) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/brief/turn`, {
      method: 'POST',
      body: JSON.stringify({ content, sourceType, priorMessages, userTimezone }),
    }) as Promise<{
      ok: boolean;
      assistantMessage: string;
      brief: unknown;
      extractionConfidence: number;
      fallbackUsed: boolean;
      unresolvedItems: Array<{ field: string; rawText: string; reason: string; suggestion: string }>;
      fieldCompleteness: Record<string, 'populated' | 'empty' | 'partial'>;
      clarificationQuestion: { field: string; question: string; reason: string; priority: 1 | 2 } | null;
      suggestedTitle: string | null;
      proposedSchedule: string | null;
      proposedRecurrence: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | null;
      proposedAgenda: Array<{ title: string; durationMin: number; type: 'update' | 'discussion' | 'decision'; owner: string | null }> | null;
    }>,
  /**
   * Mark the brief as finalized by the leader. Hard-gated server-side on at
   * least one meetingGoal. Triggers brief→governanceConfig translation onto
   * the draft meeting. Idempotent.
   */
  complete: (meetingId: string) =>
    apiRequest(`/meetings/${encodeURIComponent(meetingId)}/brief/complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    }) as Promise<{ ok: true; brief: unknown }>,
};

/** Lightweight email deliverability check (syntax + DNS) for catching typos. */
export const emailAPI = {
  verify: (emails: string[]) =>
    apiRequest('/email/verify', {
      method: 'POST',
      body: JSON.stringify({ emails }),
    }) as Promise<{ results: Array<{ email: string; valid: boolean; reason: string }> }>,
};

export default {
  auth: authAPI,
  meetings: meetingsAPI,
  brief: briefAPI,
  agenda: agendaAPI,
  calendar: calendarAPI,
  bot: botAPI,
  cppBot: cppBotAPI,
  zoom: zoomAPI,
  interrupt: interruptAPI,
  governance: governanceAPI,
  analytics: analyticsAPI,
  settings: settingsAPI,
  org: orgAPI,
  teams: teamsAPI,
  badges: badgesAPI,
  support: supportAPI,
  stripe: stripeAPI,
  setup: setupAPI,
  platformAdmin: platformAdminAPI,
  connectors: {
    getCatalog: () => apiRequest('/connectors/catalog'),
    listConnections: () => apiRequest('/connectors/connections'),
    createConnection: (data: { connectorKey: string; label?: string; feedMode?: string }) =>
      apiRequest('/connectors/connections', { method: 'POST', body: JSON.stringify(data) }),
    deleteConnection: (id: string) =>
      apiRequest(`/connectors/connections/${id}`, { method: 'DELETE' }),
    uploadFile: (id: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiRequestMultipart(`/connectors/connections/${id}/upload`, formData);
    },
    getActivity: (id: string) => apiRequest(`/connectors/connections/${id}/activity`),
  },
  diagnostic: {
    uploadTranscript: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiRequestMultipart('/diagnostics/upload', formData);
    },
    getEvidenceStats: () => apiRequest('/diagnostics/evidence/stats'),
    getSources: () => apiRequest('/diagnostics/sources'),
    triggerRun: () => apiRequest('/diagnostics/run', { method: 'POST' }),
    getLatestRun: () => apiRequest('/diagnostics/run/latest'),
    getRun: (runId: string) => apiRequest(`/diagnostics/run/${runId}`),
    deleteEvidence: (id: string) => apiRequest(`/diagnostics/evidence/${id}`, { method: 'DELETE' }),
  },
  integrationV2: {
    getStatus: () => apiRequest('/admin/integrations-v2/status'),
    listConnections: () => apiRequest('/admin/integrations-v2/connections'),
    getConnection: (id: string) => apiRequest(`/admin/integrations-v2/connections/${id}`),
    testConnection: (id: string) => apiRequest(`/admin/integrations-v2/connections/${id}/test`, { method: 'POST' }),
    triggerSync: (id: string, mode = 'incremental') => apiRequest(`/admin/integrations-v2/connections/${id}/sync`, { method: 'POST', body: JSON.stringify({ mode }) }),
    listRepositories: () => apiRequest('/admin/integrations-v2/github/repositories'),
    listPullRequests: (params?: Record<string, string>) => apiRequest(`/admin/integrations-v2/github/pull-requests${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    getPullRequest: (id: string) => apiRequest(`/admin/integrations-v2/github/pull-requests/${id}`),
    listChannels: () => apiRequest('/admin/integrations-v2/slack/channels'),
    listMessages: (params?: Record<string, string>) => apiRequest(`/admin/integrations-v2/slack/messages${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    listPeople: (params?: Record<string, string>) => apiRequest(`/admin/integrations-v2/identity/people${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    listIngressEvents: (params?: Record<string, string>) => apiRequest(`/admin/integrations-v2/ingress-events${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    getIngressEvent: (id: string) => apiRequest(`/admin/integrations-v2/ingress-events/${id}`),
    listSyncStates: () => apiRequest('/admin/integrations-v2/sync-states'),
    resetSyncCursor: (id: string) => apiRequest(`/admin/integrations-v2/sync-states/${id}/reset`, { method: 'POST', body: JSON.stringify({ confirm: true }) }),
    retrySyncState: (id: string) => apiRequest(`/admin/integrations-v2/sync-states/${id}/retry`, { method: 'POST' }),
    getKnowledgeStats: () => apiRequest('/admin/integrations-v2/knowledge/stats'),
    listCalendarEvents: () => apiRequest('/admin/integrations-v2/calendar/events'),
    runOracleComparison: (repoFullName: string) => apiRequest('/admin/integrations-v2/github/compare', { method: 'POST', body: JSON.stringify({ repoFullName }) }),
    listDecisions: (params?: Record<string, string>) => apiRequest(`/admin/integrations-v2/decisions${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    getDecision: (id: string) => apiRequest(`/admin/integrations-v2/decisions/${id}`),
    getDecisionStats: () => apiRequest('/admin/integrations-v2/decisions/stats'),
    listQuestions: (params?: Record<string, string>) => apiRequest(`/admin/integrations-v2/questions${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    getQuestionStats: () => apiRequest('/admin/integrations-v2/questions/stats'),
    getPriorities: () => apiRequest('/admin/integrations-v2/priorities'),
    getBriefing: () => apiRequest('/admin/integrations-v2/priorities/briefing'),
    runPrioritization: () => apiRequest('/admin/integrations-v2/priorities/run', { method: 'POST' }),
    getPatterns: () => apiRequest('/admin/integrations-v2/memory/patterns'),
    getActionOutcomes: () => apiRequest('/admin/integrations-v2/memory/outcomes'),
    getCrossDomainInsights: () => apiRequest('/admin/integrations-v2/memory/insights'),
  },
  pipeline: {
    getStatus: () => apiRequest('/admin/pipeline/status'),
    inspectDecisions: () => apiRequest('/admin/pipeline/inspect/decisions'),
    listMeetings: () => apiRequest('/admin/pipeline/meetings'),
    runL1Sync: () => apiRequest('/admin/pipeline/l1-sync', { method: 'POST' }),
    runL2Identity: () => apiRequest('/admin/pipeline/l2-identity', { method: 'POST' }),
    runL3Extraction: (params?: Record<string, unknown>) => apiRequest('/admin/pipeline/l3-extraction', { method: 'POST', body: JSON.stringify(params ?? {}) }),
    runL4Sweep: (params?: Record<string, unknown>) => apiRequest('/admin/pipeline/l4-sweep', { method: 'POST', body: JSON.stringify(params ?? {}) }),
    runL4Reconcile: () => apiRequest('/admin/pipeline/l4-reconcile', { method: 'POST' }),
    runL4L8Tick: (params?: Record<string, unknown>) => apiRequest('/admin/pipeline/l4-l8-tick', { method: 'POST', body: JSON.stringify(params ?? {}) }),
    runL7L8Living: () => apiRequest('/admin/pipeline/l7-l8-living', { method: 'POST' }),
    runL5Intel: () => apiRequest('/admin/pipeline/l5-intel', { method: 'POST' }),
    // Learning engine + decision spine (the SoR path — cleanup → fold → QE cadence, inline)
    runStage: (params?: Record<string, unknown>) => apiRequest('/admin/pipeline/stage', { method: 'POST', body: JSON.stringify(params ?? {}) }),
    runMeeting: (params?: Record<string, unknown>) => apiRequest('/admin/pipeline/run/meeting', { method: 'POST', body: JSON.stringify(params ?? {}) }),
    runAll: () => apiRequest('/admin/pipeline/run/all', { method: 'POST' }),
    runLearning: () => apiRequest('/admin/pipeline/run/learning', { method: 'POST' }),
    runFold: () => apiRequest('/admin/pipeline/run/fold', { method: 'POST' }),
    runQe: () => apiRequest('/admin/pipeline/run/qe', { method: 'POST' }),
    // Admin-editable comprehensive-cleanup prompt (view / save override / reset to code default)
    getCleanupPrompt: () => apiRequest('/admin/pipeline/cleanup-prompt'),
    setCleanupPrompt: (prompt: string) => apiRequest('/admin/pipeline/cleanup-prompt', { method: 'PUT', body: JSON.stringify({ prompt }) }),
    resetCleanupPrompt: () => apiRequest('/admin/pipeline/cleanup-prompt', { method: 'DELETE' }),
    previewReset: (scope: string) => apiRequest(`/admin/pipeline/reset/preview?scope=${scope}`),
    resetLearning: (params?: Record<string, unknown>) => apiRequest('/admin/pipeline/reset/learning', { method: 'POST', body: JSON.stringify(params ?? {}) }),
    resetL1: () => apiRequest('/admin/pipeline/reset/l1', { method: 'POST' }),
    resetL2: () => apiRequest('/admin/pipeline/reset/l2', { method: 'POST' }),
    resetL3: () => apiRequest('/admin/pipeline/reset/l3', { method: 'POST' }),
    resetL4L8: () => apiRequest('/admin/pipeline/reset/l4-l8', { method: 'POST' }),
    resetAll: () => apiRequest('/admin/pipeline/reset/all', { method: 'POST' }),
  },
  demo: {
    getStatus: () => apiRequest('/admin/rithmo-demo/status'),
    setup: () => apiRequest('/admin/rithmo-demo/setup', { method: 'POST', body: JSON.stringify({ clientRequestId: String(Date.now()) }) }),
    saveArtifact: (tiers: Array<{ plan: string; priceCents: number; billingPeriod: string }>) => apiRequest('/admin/rithmo-demo/artifact', { method: 'POST', body: JSON.stringify({ clientRequestId: String(Date.now()), tiers }) }),
    sync: () => apiRequest('/admin/rithmo-demo/sync', { method: 'POST', body: JSON.stringify({ clientRequestId: String(Date.now()) }) }),
    agentWithoutRithmo: () => apiRequest('/admin/rithmo-demo/agents/without-rithmo/run', { method: 'POST', body: JSON.stringify({ clientRequestId: String(Date.now()) }) }),
    agentWithRithmo: () => apiRequest('/admin/rithmo-demo/agents/with-rithmo/run', { method: 'POST', body: JSON.stringify({ clientRequestId: String(Date.now()) }) }),
    reset: () => apiRequest('/admin/rithmo-demo/reset', { method: 'POST', body: JSON.stringify({ clientRequestId: String(Date.now()) }) }),
  },
  decisionIntel: {
    getOverview: () => apiRequest('/admin/decision-intel/overview'),
    getClosingFlow: (params?: Record<string, string>) => apiRequest(`/admin/decision-intel/closing-flow${params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : ''}`),
    getFlowMap: () => apiRequest('/admin/decision-intel/flow-map'),
    getSegment: (dim: string, key: string) => apiRequest(`/admin/decision-intel/segments/${dim}/${encodeURIComponent(key)}`),
    listDecisions: (params?: Record<string, string>) => apiRequest(`/admin/decision-intel/decisions${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    getSubjects: (params?: Record<string, string>) => apiRequest(`/admin/decision-intel/subjects${params ? '?' + new URLSearchParams(params).toString() : ''}`),
    getSubjectAsOf: (id: string, dateMs: number) => apiRequest(`/admin/decision-intel/subjects/${id}/as-of?date=${dateMs}`),
    getCoverage: () => apiRequest('/admin/decision-intel/coverage'),
    setAuthority: (id: string, action: 'confirm' | 'contest' | 'clear') => apiRequest(`/admin/decision-intel/decisions/${id}/authority`, { method: 'POST', body: JSON.stringify({ action }) }),
    getDecision: (id: string) => apiRequest(`/admin/decision-intel/decisions/${id}`),
    getRelated: (id: string) => apiRequest(`/admin/decision-intel/decisions/${id}/related`),
    getReadyQueue: () => apiRequest('/admin/decision-intel/ready-queue'),
    getLinks: (kind?: string) => apiRequest(`/admin/decision-intel/links${kind ? `?kind=${kind}` : ''}`),
    getEngineStatus: () => apiRequest('/admin/decision-intel/status'),
  },
};
